/**
 * TapNpay PWA — translation catalogue.
 *
 * Closes ULTRAREVIEW_2026-05-01 [Axe 6 P1]: pre-Vague-F.2.2 the PWA shipped
 * with `<html lang="en">` while every visible string was hard-coded French.
 * This module is the canonical source of truth — every screen consumes it
 * through `useT()` so adding a language is a one-file change.
 *
 * Three locales:
 *   - fr (default, Maroc)
 *   - en (international guests)
 *   - ar (Maroc Arabic / RTL)
 *
 * Keys are dot-separated by feature: `common.*`, `cart.*`, `checkout.*`,
 * `split.*`, `confirmation.*`. Add new keys here BEFORE referencing them
 * — TS will fail closed otherwise.
 */

export type Locale = 'fr' | 'en' | 'ar';

interface Messages {
  common: {
    cancel: string;
    confirm: string;
    back: string;
    continue: string;
    loading: string;
    retry: string;
    error_generic: string;
    total: string;
    subtotal: string;
    tax: string;
    tip: string;
    yes: string;
    no: string;
    save: string;
    pay: string;
    share: string;
    close: string;
    edit: string;
    delete: string;
    saving: string;
    saved: string;
  };
  profile_edit: {
    title: string;
    full_name_label: string;
    full_name_placeholder: string;
    phone_label: string;
    phone_placeholder: string;
    phone_country_prefix: string;
    avatar_label: string;
    avatar_choose: string;
    avatar_remove: string;
    avatar_uploading: string;
    error_phone_format: string;
    error_avatar_too_large: string;
    error_avatar_type: string;
    error_save: string;
    success_saved: string;
  };
  account_delete: {
    title: string;
    step1_warning_heading: string;
    step1_warning_body: string;
    step1_acknowledge: string;
    step1_continue: string;
    step2_heading: string;
    step2_body: string;
    step2_token_label: string;
    step2_token_placeholder: string;
    step2_token_required: string;
    step2_submit: string;
    deleting: string;
    error: string;
    success_toast: string;
  };
  preferences: {
    title: string;
    language_label: string;
    theme_label: string;
    theme_auto: string;
    theme_light: string;
    theme_dark: string;
    currency_label: string;
    currency_v2_disabled: string;
  };
  privacy: {
    title: string;
    intro: string;
    consent_marketing: string;
    consent_marketing_desc: string;
    consent_analytics: string;
    consent_analytics_desc: string;
    consent_video_surveillance: string;
    consent_video_surveillance_desc: string;
    consent_third_party: string;
    consent_third_party_desc: string;
    saving: string;
    saved: string;
    error_save: string;
  };
  payment_methods: {
    title: string;
    intro: string;
    empty_title: string;
    empty_body: string;
    remove: string;
    remove_confirm: string;
    error_load: string;
    error_remove: string;
  };
  support: {
    title: string;
    tab_help: string;
    tab_contact: string;
    tab_about: string;
    help_intro: string;
    help_q1: string;
    help_a1: string;
    help_q2: string;
    help_a2: string;
    help_q3: string;
    help_a3: string;
    help_q4: string;
    help_a4: string;
    help_q5: string;
    help_a5: string;
    contact_subject_label: string;
    contact_subject_placeholder: string;
    contact_message_label: string;
    contact_message_placeholder: string;
    contact_email_label: string;
    contact_email_placeholder: string;
    contact_send: string;
    contact_sending: string;
    contact_success: string;
    contact_error: string;
    contact_email_invalid: string;
    contact_message_required: string;
    about_version: string;
    about_legal_privacy: string;
    about_legal_terms: string;
    about_legal_cgu: string;
    about_copyright: string;
  };
  loyalty_actions: {
    share_title: string;
    share_text: string;
    share_clipboard_copied: string;
    share_unavailable: string;
    invite_title: string;
    invite_text: string;
    invite_clipboard_copied: string;
    how_to_earn_title: string;
    how_to_earn_intro: string;
    how_to_earn_rule1: string;
    how_to_earn_rule2: string;
    how_to_earn_rule3: string;
    promo_title: string;
    promo_intro: string;
    promo_code_label: string;
    promo_code_placeholder: string;
    promo_apply: string;
    promo_applying: string;
    promo_success: string;
    promo_error_format: string;
    promo_error_invalid: string;
    promo_error_generic: string;
    terms_title: string;
    terms_body: string;
  };
  loyalty_tier: {
    program_label: string;
    member_label: string;
    next_in: string;
    max_reached: string;
    equivalent_in_mad: string;
  };
  cart: {
    title: string;
    empty: string;
    browse_menu: string;
    item_count_one: string;
    item_count_other: string;
    proceed_checkout: string;
    /* AUDIT-2026-05-31: migrate hardcoded labels in CartPage/CheckoutPage */
    summary_subtotal: string;
    summary_vat: string;
    summary_tip: string;
    summary_total: string;
    clear_all: string;
  };
  checkout: {
    title: string;
    paying: string;
    method_card: string;
    method_apple_pay: string;
    method_google_pay: string;
    method_zazu: string;
    method_cash: string;
    method_cash_subtitle: string;
    submit: string;
    success: string;
    failure: string;
    cash_disabled: string;
  };
  cash_wait: {
    title: string;
    subtitle: string;
    order_number_label: string;
    amount_to_pay: string;
    instructions: string;
    status_awaiting: string;
    status_received: string;
    status_received_body: string;
    error_title: string;
    error_body: string;
    call_waiter_button: string;
    back_to_menu: string;
  };
  split: {
    title: string;
    mode_equal: string;
    mode_custom: string;
    mode_by_item: string;
    payers_count: string;
    your_share: string;
    share_link_title: string;
    share_link_subtitle: string;
    share_link_copy: string;
    share_link_copied: string;
    share_link_native: string;
    share_link_unavailable: string;
    /* AUDIT-2026-05-31: migrate hardcoded labels in the 4 split pages */
    equal_title: string;
    equal_people_label: string;
    equal_total_label: string;
    equal_you_pay: string;
    custom_title: string;
    custom_prompt: string;
    custom_total_order: string;
    custom_remaining: string;
    by_item_total: string;
    by_item_info: string;
    by_item_my_items: string;
  };
  confirmation: {
    title: string;
    subtitle: string;
    receipt_email_label: string;
    leave_review: string;
    new_order: string;
  };
  language: {
    label: string;
    fr: string;
    en: string;
    ar: string;
  };
  menu: {
    unavailable: string;
    all_menu: string;
    no_items: string;
    your_addition: string;
    nfc_welcome: string;
    fallback_store_name: string;
    aria_back: string;
    aria_cart: string;
    aria_add: string;
    contains_allergen: string;
    search_placeholder: string;
    featured_badge: string;
  };
  bottom_nav: {
    aria_label: string;
    menu: string;
    scan: string;
    orders: string;
    account: string;
  };
  profile: {
    title: string;
    aria_back: string;
    aria_settings: string;
    aria_edit: string;
    aria_avatar: string;
    not_signed_in_title: string;
    not_signed_in_body: string;
    sign_in: string;
    user_fallback: string;
    empty_value: string;
    section_preferences: string;
    section_notifications: string;
    section_payment: string;
    section_account: string;
    section_support: string;
    pref_language: string;
    pref_theme: string;
    pref_currency: string;
    pref_theme_auto: string;
    pref_currency_value: string;
    notif_email_receipts: string;
    notif_push: string;
    notif_sms: string;
    payment_cards: string;
    payment_cards_count_zero: string;
    payment_receipts: string;
    account_edit: string;
    account_privacy: string;
    account_delete: string;
    support_help: string;
    support_contact: string;
    support_about: string;
    stat_orders: string;
    stat_points: string;
    log_out: string;
    logging_out: string;
  };
  loyalty: {
    title: string;
    aria_back: string;
    aria_share: string;
    points_label: string;
    sign_in_title: string;
    sign_in_cta: string;
    action_how_to_earn: string;
    action_promo_codes: string;
    action_invite_friend: string;
    select_store_empty: string;
    rewards_loading: string;
    rewards_empty: string;
    recent_activity: string;
    activity_loading: string;
    activity_empty: string;
    activity_fallback_label: string;
    terms_cta: string;
    points_unit: string;
    empty_value: string;
  };
  reservation: {
    title: string;
    aria_back: string;
    not_found_title: string;
    not_found_body: string;
    not_found_cta: string;
    loading: string;
    date_label: string;
    time_label: string;
    slots_loading: string;
    slots_error: string;
    slots_empty: string;
    party_size_label: string;
    guests_label: string;
    aria_decrement: string;
    aria_increment: string;
    contact_label: string;
    full_name_label: string;
    full_name_placeholder: string;
    phone_label: string;
    email_label: string;
    email_placeholder: string;
    request_label: string;
    request_placeholder: string;
    submit_cta: string;
    submitting: string;
    footer_cancellation: string;
    error_generic: string;
    success_title: string;
    success_body: string;
    success_restaurant_label: string;
    success_date_label: string;
    success_time_label: string;
    success_guests_label: string;
    success_name_label: string;
    success_calendar_cta: string;
    success_my_reservations_cta: string;
    calendar_event_title: string;
    calendar_event_details: string;
    weekday_sun: string;
    weekday_mon: string;
    weekday_tue: string;
    weekday_wed: string;
    weekday_thu: string;
    weekday_fri: string;
    weekday_sat: string;
  };
  nps: {
    aria_skip: string;
    header_brand: string;
    prompt_initial: string;
    prompt_after: string;
    subtitle: string;
    scale_low: string;
    scale_mid: string;
    scale_high: string;
    aria_score: string;
    detractor_title: string;
    passive_title: string;
    promoter_title: string;
    detractor_reason_slow: string;
    detractor_reason_hard: string;
    detractor_reason_payment: string;
    detractor_reason_glitches: string;
    detractor_reason_other: string;
    promoter_reason_fast: string;
    promoter_reason_simple: string;
    promoter_reason_convenient: string;
    promoter_reason_no_download: string;
    promoter_reason_secure: string;
    promoter_reason_other: string;
    details_label: string;
    details_placeholder_detractor: string;
    details_placeholder_passive: string;
    google_review_prompt: string;
    google_review_cta: string;
    google_review_unavailable: string;
    submit_cta: string;
    submitting: string;
    thank_you_title: string;
    thank_you_subtitle: string;
    returning_home: string;
    footer_brand: string;
  };
  order_history: {
    title: string;
    aria_back: string;
    filter_all: string;
    filter_paid: string;
    filter_cancelled: string;
    empty_title: string;
    empty_body: string;
    total_label: string;
    reorder_cta: string;
    status_submitted: string;
    status_preparing: string;
    status_ready: string;
    status_served: string;
    status_paid: string;
    status_cancelled: string;
  };
  my_orders: {
    title: string;
    aria_back: string;
    empty_title: string;
    empty_body: string;
    order_fallback: string;
    total_label: string;
    request_call_waiter: string;
    request_bill: string;
    cta_order_more: string;
    toast_waiter_notified: string;
    toast_bill_requested: string;
    status_pending: string;
    status_confirmed: string;
    status_preparing: string;
    status_ready: string;
    status_delivered: string;
    status_cancelled: string;
  };
  call_waiter: {
    fab_label: string;          // floating button label on MenuPage
    sending: string;             // in-flight feedback
    sent: string;                // success toast (1-shot)
    cooldown: string;            // grey-button copy during the 10s UI cooldown
    error: string;               // generic failure
    dedup_short: string;         // "we just sent — wait a moment" (15s window)
    rate_limited: string;        // 5-per-60s server enforcement
  };
  nfc_waiting: {
    unsupported_title: string;
    unsupported_body: string;
    denied_title: string;
    denied_body: string;
    error_title: string;
    error_body: string;
    timeout_title: string;
    timeout_body: string;
    ready_title: string;
    ready_body: string;
    time_remaining: string;
    use_qr_cta: string;
    retry_nfc: string;
    cancel: string;
  };
  resolve_table: {
    welcome_fallback: string;
    opening_menu: string;
    ready_to_order: string;
    not_found_title: string;
    not_found_body: string;
    rescan_cta: string;
    call_server_cta: string;
    powered_by: string;
  };
  geofence: {
    requesting: string;
    permission_title: string;
    permission_body: string;
    permission_cta: string;
    outside_title: string;
    outside_body: string;
    outside_call_waiter: string;
    outside_retry: string;
    unsupported_title: string;
    unsupported_body: string;
    inconsistent_title: string;
    inconsistent_body: string;
    verified_badge: string;
  };
  // UX-02 (Sprint 2) — ConfirmationPage 6 ZAZU verify states.
  // Cf audit-tapnpay-2026-05-15/ux-02-implementation-plan.md §UX-DEC-4.
  zazu_verify: {
    success_title: string;
    success_body: string;
    pending_title: string;
    pending_body: string;
    pending_cta_retry: string;
    pending_cta_orders: string;
    canceled_title: string;
    canceled_body: string;
    canceled_cta_back: string;
    failed_title: string;
    failed_body: string;
    failed_cta_retry: string;
    error_title: string;
    error_body: string;
    error_cta_retry: string;
  };
  qr_bill: {
    error_missing_token: string;
    error_expired: string;
    error_unavailable: string;
    error_payment_init: string;
    error_no_payment_url: string;
    bill_unavailable_title: string;
    back_cta: string;
    item_fallback: string;
    subtotal: string;
    tax: string;
    tip_legend: string;
    tip_custom: string;
    tip_custom_placeholder: string;
    contact_legend: string;
    contact_email_label: string;
    contact_sms_label: string;
    email_placeholder: string;
    phone_placeholder: string;
    total_label: string;
    pay_cta: string;
    pay_opening: string;
    pay_methods: string;
    confirming_title: string;
    confirming_body: string;
    rating_title: string;
    rating_body: string;
    rating_low_placeholder: string;
    rating_send_feedback: string;
    rating_skip: string;
    done_title: string;
    done_body: string;
  };
  review_prompt: {
    aria_stars: string;
    title: string;
    body_with_store: string;
    body_generic: string;
    review_google: string;
    review_unconfigured: string;
    review_tripadvisor: string;
    coming_soon: string;
    skip_cta: string;
  };
  gift_card_purchase: {
    aria_back: string;
    title: string;
    hero_title: string;
    hero_valid_at: string;
    section_amount: string;
    custom_amount_label: string;
    recipient_toggle: string;
    recipient_email_label: string;
    recipient_email_placeholder: string;
    message_label: string;
    message_placeholder: string;
    error_invalid_form: string;
    error_no_session: string;
    cta_buy: string;
    footer_email_recipient: string;
    payment_title: string;
    payment_total: string;
    payment_processing: string;
    payment_pay_cta: string;
    payment_failed: string;
  };
  gift_card_redeem: {
    aria_back: string;
    title: string;
    hero_balance_label: string;
    hero_default_code: string;
    code_label: string;
    code_placeholder: string;
    code_email_hint: string;
    code_invalid: string;
    expires_prefix: string;
    status_active: string;
    status_redeemed: string;
    status_expired: string;
    status_cancelled: string;
    history_title: string;
    history_empty: string;
    error_no_store: string;
    error_empty_cart: string;
    error_invalid: string;
    cta_apply: string;
    cta_applying: string;
    cta_applied: string;
    footer_or_at_checkout: string;
  };
  // Vague 2026-05-30 — TapNpay V1.B new client features.
  waiter_status: {
    requested: string;
    ack: string;
    en_route: string;
    resolved: string;
  };
  order_tracking: {
    title: string;
    item_pending: string;
    item_preparing: string;
    item_ready: string;
    item_served: string;
  };
  tip_chips: {
    title: string;
    custom: string;
    suggested: string;
  };
  item_notes: {
    placeholder: string;
    label: string;
  };
  recommendations: {
    title: string;
  };
  whatsapp_share: {
    btn: string;
    message: string;
  };
  order_ahead: {
    title: string;
    pickup_time: string;
    name: string;
    phone: string;
    btn_submit: string;
    confirmation: string;
  };
  stock_badge: {
    low_stock: string;
  };
  promotions_client: {
    label: string;
  };
}

const fr: Messages = {
  common: {
    cancel: 'Annuler',
    confirm: 'Confirmer',
    back: 'Retour',
    continue: 'Continuer',
    loading: 'Chargement…',
    retry: 'Réessayer',
    error_generic: 'Une erreur est survenue. Réessaye.',
    total: 'Total',
    subtotal: 'Sous-total',
    tax: 'TVA',
    tip: 'Pourboire',
    yes: 'Oui',
    no: 'Non',
    save: 'Enregistrer',
    pay: 'Payer',
    share: 'Partager',
    close: 'Fermer',
    edit: 'Modifier',
    delete: 'Supprimer',
    saving: 'Enregistrement…',
    saved: 'Enregistré.',
  },
  profile_edit: {
    title: 'Modifier mon profil',
    full_name_label: 'Nom complet',
    full_name_placeholder: 'Votre nom',
    phone_label: 'Téléphone',
    phone_placeholder: '600 000 000',
    phone_country_prefix: '+212',
    avatar_label: 'Photo de profil',
    avatar_choose: 'Choisir une photo',
    avatar_remove: 'Retirer',
    avatar_uploading: 'Envoi en cours…',
    error_phone_format: 'Numéro invalide. Format attendu : 6XXXXXXXX ou +212 6XXXXXXXX.',
    error_avatar_too_large: 'Photo trop lourde (max 2 Mo).',
    error_avatar_type: 'Format non supporté. Utilise JPG ou PNG.',
    error_save: "Impossible d'enregistrer. Réessaye.",
    success_saved: 'Profil mis à jour.',
  },
  account_delete: {
    title: 'Supprimer mon compte',
    step1_warning_heading: 'Action irréversible',
    step1_warning_body:
      'Toutes vos données (commandes, points fidélité, profil, reçus) seront supprimées sous 30 jours. Vous serez déconnecté immédiatement.',
    step1_acknowledge: "Je confirme avoir compris.",
    step1_continue: 'Continuer',
    step2_heading: 'Confirmation finale',
    step2_body:
      'Tape SUPPRIMER pour confirmer la suppression définitive de votre compte.',
    step2_token_label: 'Mot de confirmation',
    step2_token_placeholder: 'SUPPRIMER',
    step2_token_required: 'SUPPRIMER',
    step2_submit: 'Supprimer définitivement',
    deleting: 'Suppression en cours…',
    error: "La suppression n'a pas abouti. Réessaye ou contacte le support.",
    success_toast: 'Compte supprimé. Adieu 👋',
  },
  preferences: {
    title: 'Préférences',
    language_label: 'Langue',
    theme_label: 'Thème',
    theme_auto: 'Auto',
    theme_light: 'Clair',
    theme_dark: 'Sombre',
    currency_label: 'Devise',
    currency_v2_disabled: 'Disponible en V2',
  },
  privacy: {
    title: 'Confidentialité',
    intro:
      'Tu choisis ce que TapNpay peut faire avec tes données. Ces choix sont conformes CNDP et RGPD ; tu peux les modifier à tout moment.',
    consent_marketing: 'Communications marketing',
    consent_marketing_desc:
      'Recevoir des offres et nouveautés du restaurant par email ou SMS.',
    consent_analytics: 'Analyse anonyme',
    consent_analytics_desc:
      'Améliorer le service via des statistiques agrégées (sans identification).',
    consent_video_surveillance: 'Vidéo-surveillance',
    consent_video_surveillance_desc:
      "Si le restaurant filme la salle pour sécurité (CNDP). Aucun ciblage personnel.",
    consent_third_party: 'Partenaires de confiance',
    consent_third_party_desc:
      'Partager des données techniques avec nos prestataires (paiement, hébergement).',
    saving: 'Enregistrement…',
    saved: 'Préférences enregistrées.',
    error_save: "Impossible d'enregistrer le consentement. Réessaye.",
  },
  payment_methods: {
    title: 'Cartes enregistrées',
    intro:
      'Les cartes que tu as choisi de mémoriser pour tes paiements futurs.',
    empty_title: 'Aucune carte enregistrée',
    empty_body:
      "Lors d'un prochain paiement, coche « Mémoriser cette carte » pour la retrouver ici.",
    remove: 'Retirer',
    remove_confirm: 'Retirer cette carte ?',
    error_load: 'Chargement des cartes impossible. Réessaye.',
    error_remove: "La carte n'a pas pu être retirée. Réessaye.",
  },
  support: {
    title: 'Support',
    tab_help: 'Aide',
    tab_contact: 'Contact',
    tab_about: 'À propos',
    help_intro: 'Les questions les plus fréquentes.',
    help_q1: 'Comment payer mon addition ?',
    help_a1:
      "Scanne le QR code de ta table avec l'appareil photo. Tu accèdes au menu, ajoutes au panier puis payes par Apple Pay, Google Pay, carte ou ZAZU.",
    help_q2: 'Mes données sont-elles protégées ?',
    help_a2:
      'TapNpay est conforme RGPD et CNDP. Tes données sont chiffrées (AES-256) et tu peux les supprimer à tout moment depuis Compte → Supprimer mon compte.',
    help_q3: 'Comment partager une addition ?',
    help_a3:
      "Au panier, choisis « Diviser l'addition ». Trois modes : parts égales, par article, ou montant libre.",
    help_q4: 'Comment ajouter un pourboire ?',
    help_a4:
      "Au panier, clique « Add Tip » pour ouvrir la modale. Choisis 0/5/10/15 % ou un montant libre.",
    help_q5: "Que se passe-t-il si le réseau coupe pendant le paiement ?",
    help_a5:
      "Ton panier et ta session de paiement sont sauvegardés localement. Reconnecte-toi à Internet et continue où tu en étais.",
    contact_subject_label: 'Sujet',
    contact_subject_placeholder: 'Que pouvons-nous t\'aider à faire ?',
    contact_message_label: 'Message',
    contact_message_placeholder: 'Décris ta demande en quelques lignes…',
    contact_email_label: 'Email pour la réponse',
    contact_email_placeholder: 'toi@example.com',
    contact_send: 'Envoyer',
    contact_sending: 'Envoi…',
    contact_success: 'Merci, on revient vers toi sous 48 h.',
    contact_error: "L'envoi a échoué. Tu peux nous écrire directement à support@tapnpay.ma.",
    contact_email_invalid: 'Email invalide.',
    contact_message_required: 'Décris ta demande avant d\'envoyer.',
    about_version: 'TapNpay PWA · v{{version}}',
    about_legal_privacy: 'Politique de confidentialité',
    about_legal_terms: 'Conditions générales',
    about_legal_cgu: 'CGU',
    about_copyright: '© {{year}} SecurePOS · TapNpay',
  },
  loyalty_actions: {
    share_title: 'Programme fidélité TapNpay',
    share_text:
      'Découvre TapNpay et gagne des points fidélité à chaque addition. Scanne, paye, profite.',
    share_clipboard_copied: 'Lien copié.',
    share_unavailable: 'Partage indisponible. Lien copié dans le presse-papiers.',
    invite_title: 'Invite un ami sur TapNpay',
    invite_text:
      "Rejoins TapNpay avec mon code et on gagne des points tous les deux à votre première addition.",
    invite_clipboard_copied: 'Lien d\'invitation copié.',
    how_to_earn_title: 'Comment gagner des points',
    how_to_earn_intro:
      'Tes points TapNpay se cumulent automatiquement à chaque paiement validé. 1 point = 0,10 MAD de récompense (taux indicatif, peut varier par enseigne).',
    how_to_earn_rule1:
      '1 MAD payé via TapNpay = 1 point fidélité crédité après paiement confirmé.',
    how_to_earn_rule2:
      'Première addition d\'un ami invité par toi = +50 points bonus (pour vous deux).',
    how_to_earn_rule3:
      'Avis Google ★★★★ ou + après paiement = +25 points (1 fois par enseigne).',
    promo_title: 'Code promotionnel',
    promo_intro:
      'Saisis un code promotionnel pour bénéficier d\'une remise ou de points bonus.',
    promo_code_label: 'Code',
    promo_code_placeholder: 'PROMO2026',
    promo_apply: 'Appliquer',
    promo_applying: 'Application…',
    promo_success: 'Code appliqué.',
    promo_error_format: 'Format invalide. 4 à 16 caractères, lettres ou chiffres.',
    promo_error_invalid: 'Code invalide ou expiré.',
    promo_error_generic: 'Application impossible. Réessaye.',
    terms_title: 'Conditions du programme',
    terms_body:
      "Le programme fidélité TapNpay est offert par chaque restaurant partenaire. Les points sont cumulables sans expiration tant que le compte est actif. Le restaurant peut modifier ou retirer le programme à tout moment ; tu seras notifié au moins 30 jours avant. Les points n'ont aucune valeur monétaire échangeable et ne sont pas cessibles. La fraude (codes générés, multi-comptes, etc.) entraîne la révocation du compte. Voir la politique complète sur tapnpay.ma/legal/loyalty.",
  },
  loyalty_tier: {
    program_label: 'Programme Fidélité',
    member_label: 'Membre {{tier}}',
    next_in: 'Niveau {{nextTier}} dans {{delta}} pts',
    max_reached: 'Niveau max atteint',
    equivalent_in_mad: '≈ {{amount}} MAD en récompenses',
  },
  cart: {
    title: 'Panier',
    empty: 'Ton panier est vide.',
    browse_menu: 'Voir le menu',
    item_count_one: '{{count}} article',
    item_count_other: '{{count}} articles',
    proceed_checkout: 'Passer à la caisse',
    summary_subtotal: 'Sous-total',
    summary_vat: 'TVA (20%)',
    summary_tip: 'Pourboire',
    summary_total: 'Total',
    clear_all: 'Tout retirer',
  },
  checkout: {
    title: 'Paiement',
    paying: 'Paiement en cours…',
    method_card: 'Carte bancaire',
    method_apple_pay: 'Apple Pay',
    method_google_pay: 'Google Pay',
    method_zazu: 'ZAZU',
    method_cash: 'Espèces',
    method_cash_subtitle: 'Le serveur encaissera à votre table',
    submit: 'Régler {{amount}}',
    success: 'Merci ! Paiement confirmé.',
    failure: 'Le paiement n\'a pas abouti.',
    cash_disabled: 'Le paiement en espèces n\'est pas accepté dans ce restaurant.',
  },
  cash_wait: {
    title: 'Appelez votre serveur',
    subtitle: 'Présentez cet écran à votre serveur pour régler en espèces.',
    order_number_label: 'Commande N°',
    amount_to_pay: 'Montant à régler',
    instructions: 'Votre commande est envoyée en cuisine. Le serveur viendra encaisser à votre table.',
    status_awaiting: 'En attente d\'encaissement…',
    status_received: 'Paiement reçu',
    status_received_body: 'Merci ! Votre reçu fiscal est en cours d\'émission.',
    error_title: 'Erreur',
    error_body: 'Impossible de vérifier le statut. Demandez confirmation à votre serveur.',
    call_waiter_button: 'Appeler le serveur',
    back_to_menu: 'Retour au menu',
  },
  split: {
    title: 'Partager l\'addition',
    mode_equal: 'Parts égales',
    mode_custom: 'Montant libre',
    mode_by_item: 'Par plat',
    payers_count: 'Nombre de payeurs',
    your_share: 'Ta part',
    share_link_title: 'Inviter un autre payeur',
    share_link_subtitle:
      'Envoie ce lien à la personne qui paye le reste. Il expire avec ta session.',
    share_link_copy: 'Copier le lien',
    share_link_copied: 'Lien copié.',
    share_link_native: 'Partager…',
    share_link_unavailable: 'Le lien sera disponible après ta confirmation.',
    equal_title: 'Parts égales',
    equal_people_label: 'Nombre de personnes',
    equal_total_label: 'Total',
    equal_you_pay: 'Vous payez',
    custom_title: 'Montant libre',
    custom_prompt: 'Combien voulez-vous payer ?',
    custom_total_order: 'Total commande',
    custom_remaining: 'Reste pour les autres',
    by_item_total: 'Total',
    by_item_info: 'Cochez les articles que vous payez. Le reste sera à régler par les autres convives.',
    by_item_my_items: 'Mes articles',
  },
  confirmation: {
    title: 'Commande confirmée',
    subtitle: 'Le restaurant a reçu ta commande.',
    receipt_email_label: 'Reçu envoyé à',
    leave_review: 'Laisser un avis',
    new_order: 'Nouvelle commande',
  },
  language: {
    label: 'Langue',
    fr: 'Français',
    en: 'English',
    ar: 'العربية',
  },
  menu: {
    unavailable: 'Menu indisponible',
    all_menu: 'Tout le menu',
    no_items: 'Aucun article dans cette catégorie.',
    your_addition: 'Ton addition',
    nfc_welcome: 'Bienvenue chez {{store}}',
    fallback_store_name: 'TapNpay',
    aria_back: 'Retour',
    aria_cart: 'Panier',
    aria_add: 'Ajouter',
    contains_allergen: 'Contient {{allergen}}',
    search_placeholder: 'Rechercher un plat…',
    featured_badge: 'POPULAIRE',
  },
  bottom_nav: {
    aria_label: 'Navigation principale',
    menu: 'Menu',
    scan: 'Scanner',
    orders: 'Commandes',
    account: 'Compte',
  },
  profile: {
    title: 'Compte',
    aria_back: 'Retour',
    aria_settings: 'Réglages',
    aria_edit: 'Modifier',
    aria_avatar: 'Avatar',
    not_signed_in_title: 'Pas connecté',
    not_signed_in_body:
      'Connectez-vous pour gérer votre profil, vos cartes et vos préférences.',
    sign_in: 'Se connecter',
    user_fallback: 'Utilisateur',
    empty_value: '—',
    section_preferences: 'Préférences',
    section_notifications: 'Notifications',
    section_payment: 'Paiement',
    section_account: 'Compte',
    section_support: 'Support',
    pref_language: 'Langue',
    pref_theme: 'Thème',
    pref_currency: 'Devise',
    pref_theme_auto: 'Auto',
    pref_currency_value: 'MAD',
    notif_email_receipts: 'Reçus par email',
    notif_push: 'Notifications push',
    notif_sms: 'Confirmation SMS',
    payment_cards: 'Cartes enregistrées',
    payment_cards_count_zero: '0 carte',
    payment_receipts: 'Reçus de paiement',
    account_edit: 'Modifier le profil',
    account_privacy: 'Confidentialité',
    account_delete: 'Supprimer mon compte',
    support_help: 'Aide & FAQ',
    support_contact: 'Contacter TapNpay',
    support_about: 'À propos · v1.0.0',
    stat_orders: 'Commandes',
    stat_points: 'Points',
    log_out: 'Se déconnecter',
    logging_out: 'Déconnexion…',
  },
  loyalty: {
    title: 'Mes Points',
    aria_back: 'Retour',
    aria_share: 'Partager',
    points_label: 'Points TapNpay',
    sign_in_title: 'Connectez-vous pour voir vos points',
    sign_in_cta: 'Se connecter',
    action_how_to_earn: 'Comment gagner ?',
    action_promo_codes: 'Codes promo',
    action_invite_friend: 'Inviter un ami',
    select_store_empty: 'Sélectionnez un restaurant pour voir ses récompenses.',
    rewards_loading: 'Chargement des récompenses…',
    rewards_empty: 'Le programme fidélité sera disponible bientôt.',
    recent_activity: 'Activité récente',
    activity_loading: 'Chargement…',
    activity_empty: 'Aucune activité pour le moment.',
    activity_fallback_label: 'Activité',
    terms_cta: 'Conditions du programme',
    points_unit: 'pts',
    empty_value: '—',
  },
  reservation: {
    title: 'Réservations',
    aria_back: 'Retour',
    not_found_title: 'Restaurant introuvable',
    not_found_body: 'Vérifiez le lien ou revenez à l\'accueil.',
    not_found_cta: 'Retour à l\'accueil',
    loading: 'Chargement…',
    date_label: 'Date',
    time_label: 'Heure',
    slots_loading: 'Chargement…',
    slots_error: 'Impossible de charger les créneaux. Réessaye dans un instant.',
    slots_empty: 'Aucun créneau disponible ce jour-là.',
    party_size_label: 'Nombre de convives',
    guests_label: 'Convives',
    aria_decrement: 'Diminuer',
    aria_increment: 'Augmenter',
    contact_label: 'Coordonnées',
    full_name_label: 'Nom complet',
    full_name_placeholder: 'Votre nom',
    phone_label: 'Téléphone',
    email_label: 'Email (optionnel)',
    email_placeholder: 'vous@example.com',
    request_label: 'Demande spéciale (optionnel)',
    request_placeholder: 'Anniversaire, allergie, table en terrasse…',
    submit_cta: 'Confirmer la réservation',
    submitting: 'Confirmation…',
    footer_cancellation: 'Annulation gratuite jusqu\'à 2h avant la réservation.',
    error_generic: 'Erreur de soumission',
    success_title: 'Réservation confirmée',
    success_body: 'Un email de confirmation vous sera envoyé sous peu.',
    success_restaurant_label: 'Restaurant',
    success_date_label: 'Date',
    success_time_label: 'Heure',
    success_guests_label: 'Convives',
    success_name_label: 'Au nom de',
    success_calendar_cta: 'Ajouter au calendrier',
    success_my_reservations_cta: 'Voir mes réservations',
    calendar_event_title: 'Réservation · {{store}}',
    calendar_event_details:
      'Réservation TapNpay au nom de {{name}} pour {{count}} personne(s).',
    weekday_sun: 'Dim',
    weekday_mon: 'Lun',
    weekday_tue: 'Mar',
    weekday_wed: 'Mer',
    weekday_thu: 'Jeu',
    weekday_fri: 'Ven',
    weekday_sat: 'Sam',
  },
  nps: {
    aria_skip: 'Passer le sondage',
    header_brand: 'TAPNPAY',
    prompt_initial: 'Sur une échelle de 0 à 10, recommanderais-tu TapNpay à un ami ?',
    prompt_after: 'Ton score de recommandation',
    subtitle: 'Ton avis nous aide à nous améliorer.',
    scale_low: 'Pas du tout',
    scale_mid: 'Neutre',
    scale_high: 'Absolument',
    aria_score: 'Score {{n}}',
    detractor_title: 'Qu\'est-ce qui t\'a déçu ?',
    passive_title: 'Qu\'est-ce qui aurait pu être mieux ?',
    promoter_title: 'Génial ! Qu\'as-tu aimé ?',
    detractor_reason_slow: 'L\'app était lente',
    detractor_reason_hard: 'Difficile à utiliser',
    detractor_reason_payment: 'Problème de paiement',
    detractor_reason_glitches: 'Bugs d\'interface',
    detractor_reason_other: 'Autre',
    promoter_reason_fast: 'Rapide',
    promoter_reason_simple: 'Simple',
    promoter_reason_convenient: 'Pratique',
    promoter_reason_no_download: 'Pas de téléchargement',
    promoter_reason_secure: 'Sécurisé',
    promoter_reason_other: 'Autre',
    details_label: 'Détails (optionnel)',
    details_placeholder_detractor: 'Raconte-nous ton expérience…',
    details_placeholder_passive: 'Optionnel — ton avis nous aide à nous améliorer…',
    google_review_prompt: 'Aide-nous en laissant un avis Google ?',
    google_review_cta: 'Laisser un avis ★',
    google_review_unavailable: 'Avis indisponible',
    submit_cta: 'Envoyer mon avis',
    submitting: 'Envoi…',
    thank_you_title: 'Merci pour ton avis ✓',
    thank_you_subtitle: 'Ton avis a été enregistré.',
    returning_home: 'Retour à l\'accueil dans 3 s…',
    footer_brand: 'TAPNPAY SECURE TERMINAL',
  },
  order_history: {
    title: 'Historique des commandes',
    aria_back: 'Retour',
    filter_all: 'Tout',
    filter_paid: 'Payées',
    filter_cancelled: 'Annulées',
    empty_title: 'Aucune commande passée',
    empty_body: 'Tes commandes TapNpay apparaîtront ici.',
    total_label: 'Total',
    reorder_cta: 'Recommander',
    status_submitted: 'Envoyée',
    status_preparing: 'En préparation',
    status_ready: 'Prête',
    status_served: 'Servie',
    status_paid: 'Payée',
    status_cancelled: 'Annulée',
  },
  my_orders: {
    title: 'Mes commandes',
    aria_back: 'Retour',
    empty_title: 'Aucune commande pour le moment',
    empty_body: 'Tes commandes apparaîtront ici après paiement.',
    order_fallback: 'Commande',
    total_label: 'Total',
    request_call_waiter: 'Appeler le serveur',
    request_bill: 'Demander l\'addition',
    cta_order_more: 'Commander plus',
    toast_waiter_notified: 'Serveur prévenu.',
    toast_bill_requested: 'Demande d\'addition envoyée.',
    status_pending: 'En attente',
    status_confirmed: 'Confirmée',
    status_preparing: 'En préparation',
    status_ready: 'Prête',
    status_delivered: 'Servie',
    status_cancelled: 'Annulée',
  },
  call_waiter: {
    fab_label: 'Appeler le serveur',
    sending: 'Envoi…',
    sent: 'Serveur prévenu ✓',
    cooldown: 'Demande envoyée ✓',
    error: 'Échec — réessaie dans un instant.',
    dedup_short: 'Une demande vient d\'être envoyée. Patiente un instant.',
    rate_limited: 'Trop de demandes — réessaie dans une minute.',
  },
  nfc_waiting: {
    unsupported_title: 'NFC non pris en charge',
    unsupported_body: 'Utilise le QR code à la place.',
    denied_title: 'Permission refusée',
    denied_body: 'Autorisation NFC refusée.',
    error_title: 'Erreur NFC',
    error_body: 'Erreur lecture NFC.',
    timeout_title: 'Aucun tag détecté',
    timeout_body: '30 s sans détection.',
    ready_title: 'Approche ton téléphone',
    ready_body: 'du sticker NFC',
    time_remaining: '30 s restantes',
    use_qr_cta: 'UTILISER LE QR CODE',
    retry_nfc: 'Réessayer NFC',
    cancel: 'Annuler',
  },
  resolve_table: {
    welcome_fallback: 'Bienvenue',
    opening_menu: 'Ouverture du menu…',
    ready_to_order: 'Prêt à commander ✓',
    not_found_title: 'Table introuvable',
    not_found_body: 'Demandez à un serveur, ou rescannez le QR code.',
    rescan_cta: 'Rescanner',
    call_server_cta: 'Appeler un serveur',
    powered_by: 'Propulsé par TapNpay',
  },
  geofence: {
    requesting: 'Vérification de votre emplacement…',
    permission_title: 'Activez la localisation',
    permission_body: 'Pour payer à votre table, autorisez l’accès à votre position.',
    permission_cta: 'Autoriser',
    outside_title: 'Vous semblez être hors du restaurant',
    outside_body: 'Demandez à un serveur de prendre votre paiement.',
    outside_call_waiter: 'Appeler le serveur',
    outside_retry: 'Réessayer',
    unsupported_title: 'Localisation non disponible',
    unsupported_body: 'Votre navigateur ne supporte pas la géolocalisation. Demandez à un serveur de finaliser votre commande.',
    inconsistent_title: 'Position imprécise',
    inconsistent_body: 'Votre GPS n’est pas assez précis. Sortez à l’air libre et réessayez, ou demandez un serveur.',
    verified_badge: 'Vérifié à {{distance}} m',
  },
  zazu_verify: {
    success_title: 'Paiement confirmé',
    success_body: 'Votre paiement a bien été reçu.',
    pending_title: 'Vérification en cours',
    pending_body: 'Votre paiement peut prendre quelques secondes à être confirmé. Vous pouvez actualiser ou consulter vos commandes.',
    pending_cta_retry: 'Vérifier maintenant',
    pending_cta_orders: 'Voir mes commandes',
    canceled_title: 'Paiement annulé',
    canceled_body: 'Votre panier est sauvegardé.',
    canceled_cta_back: 'Retour au panier',
    failed_title: 'Paiement échoué',
    failed_body: 'Une erreur est survenue. Veuillez réessayer.',
    failed_cta_retry: 'Réessayer',
    error_title: 'Vérification impossible',
    error_body: 'Connexion réseau indisponible. Réessayez dans un instant.',
    error_cta_retry: 'Réessayer',
  },
  qr_bill: {
    error_missing_token: 'Lien de facture manquant.',
    error_expired: 'Cette facture a expiré.',
    error_unavailable: 'Facture {{status}}.',
    error_payment_init: 'Initialisation du paiement échouée.',
    error_no_payment_url: 'Aucune URL de paiement reçue.',
    bill_unavailable_title: 'Facture indisponible',
    back_cta: 'Retour',
    item_fallback: 'Article',
    subtotal: 'Sous-total',
    tax: 'TVA',
    tip_legend: 'Pourboire (optionnel)',
    tip_custom: 'Libre',
    tip_custom_placeholder: 'Pourboire en {{currency}}',
    contact_legend: 'Email ou téléphone (pour le reçu fiscal)',
    contact_email_label: 'Email',
    contact_sms_label: 'SMS',
    email_placeholder: 'vous@example.com',
    phone_placeholder: '+212 6XX XXX XXX',
    total_label: 'Total',
    pay_cta: 'Payer {{amount}}',
    pay_opening: 'Ouverture du paiement…',
    pay_methods: 'Apple Pay · Google Pay · Carte via ZAZU',
    confirming_title: 'Confirmation du paiement…',
    confirming_body: 'Cela prend habituellement quelques secondes.',
    rating_title: 'Merci pour votre paiement',
    rating_body: 'Comment s\'est passée votre expérience ?',
    rating_low_placeholder: 'Que pourrions-nous améliorer ? (optionnel)',
    rating_send_feedback: 'Envoyer le retour',
    rating_skip: 'Non merci',
    done_title: 'Tout est en ordre',
    done_body: 'Votre reçu fiscal arrive par email/SMS.',
  },
  review_prompt: {
    aria_stars: 'Note 5 sur 5',
    title: 'Comment était votre expérience ?',
    body_with_store: 'Aidez {{store}} en partageant votre avis.',
    body_generic: 'Partagez votre expérience.',
    review_google: 'Laisser un avis Google',
    review_unconfigured: 'Avis non encore configurés',
    review_tripadvisor: 'Laisser un avis TripAdvisor',
    coming_soon: '(Bientôt disponible)',
    skip_cta: 'Plus tard',
  },
  gift_card_purchase: {
    aria_back: 'Retour',
    title: 'Carte cadeau',
    hero_title: 'Offrir une carte cadeau',
    hero_valid_at: 'Valable chez {{store}}',
    section_amount: 'Montant',
    custom_amount_label: 'Montant libre',
    recipient_toggle: 'Pour quelqu\'un d\'autre ?',
    recipient_email_label: 'Email du destinataire',
    recipient_email_placeholder: 'email@example.com',
    message_label: 'Message (optionnel)',
    message_placeholder: 'Ajoutez un mot personnel…',
    error_invalid_form: 'Vérifiez le montant et l\'email du destinataire.',
    error_no_session: 'Session de paiement indisponible.',
    cta_buy: 'Acheter',
    footer_email_recipient: 'Le destinataire recevra le code par email.',
    payment_title: 'Paiement',
    payment_total: 'Total',
    payment_processing: 'Traitement…',
    payment_pay_cta: 'Payer — {{amount}}',
    payment_failed: 'Paiement échoué.',
  },
  gift_card_redeem: {
    aria_back: 'Retour',
    title: 'Carte cadeau',
    hero_balance_label: 'Solde restant',
    hero_default_code: 'TPN-XXX-XXX',
    code_label: 'Code de votre carte',
    code_placeholder: 'TPN-XXXX-YYYY',
    code_email_hint: 'Le code se trouve dans l\'email reçu.',
    code_invalid: 'Code invalide ou expiré.',
    expires_prefix: 'Expire {{date}}',
    status_active: 'Valide ✓',
    status_redeemed: 'Déjà utilisée',
    status_expired: 'Expirée',
    status_cancelled: 'Annulée',
    history_title: 'Historique',
    history_empty: 'Aucune utilisation pour le moment.',
    error_no_store: 'Scannez d\'abord un QR de table pour utiliser cette carte.',
    error_empty_cart: 'Votre panier est vide.',
    error_invalid: 'Code invalide',
    cta_apply: 'Utiliser sur cette addition',
    cta_applying: 'Application…',
    cta_applied: 'Appliquée — voir le panier',
    footer_or_at_checkout: 'Ou ajoutez ce code lors du paiement.',
  },
  waiter_status: {
    requested: 'Demande envoyée',
    ack: 'Reçue par le serveur',
    en_route: 'En route',
    resolved: 'Servi',
  },
  order_tracking: {
    title: 'Suivi de commande',
    item_pending: 'En attente',
    item_preparing: 'En préparation',
    item_ready: 'Prêt',
    item_served: 'Servi',
  },
  tip_chips: {
    title: 'Ajouter un pourboire ?',
    custom: 'Personnalisé',
    suggested: 'Suggéré',
  },
  item_notes: {
    placeholder: 'Notes spéciales (ex: sans oignon)',
    label: 'Notes spéciales',
  },
  recommendations: {
    title: 'Souvent commandé avec',
  },
  whatsapp_share: {
    btn: 'Partager via WhatsApp',
    message: 'Voici ma part : {{amount}} MAD — {{link}}',
  },
  order_ahead: {
    title: 'Commander à l\'avance',
    pickup_time: 'Heure de retrait',
    name: 'Votre nom',
    phone: 'Téléphone',
    btn_submit: 'Confirmer la commande',
    confirmation: 'Commande confirmée pour {{time}}',
  },
  stock_badge: {
    low_stock: 'Plus que {{n}} portion(s)',
  },
  promotions_client: {
    label: 'PROMO -{{pct}}%',
  },
};

const en: Messages = {
  common: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    back: 'Back',
    continue: 'Continue',
    loading: 'Loading…',
    retry: 'Retry',
    error_generic: 'Something went wrong. Try again.',
    total: 'Total',
    subtotal: 'Subtotal',
    tax: 'VAT',
    tip: 'Tip',
    yes: 'Yes',
    no: 'No',
    save: 'Save',
    pay: 'Pay',
    share: 'Share',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    saving: 'Saving…',
    saved: 'Saved.',
  },
  profile_edit: {
    title: 'Edit my profile',
    full_name_label: 'Full name',
    full_name_placeholder: 'Your name',
    phone_label: 'Phone',
    phone_placeholder: '600 000 000',
    phone_country_prefix: '+212',
    avatar_label: 'Profile picture',
    avatar_choose: 'Choose a photo',
    avatar_remove: 'Remove',
    avatar_uploading: 'Uploading…',
    error_phone_format: 'Invalid number. Expected format: 6XXXXXXXX or +212 6XXXXXXXX.',
    error_avatar_too_large: 'Photo too large (max 2 MB).',
    error_avatar_type: 'Unsupported format. Use JPG or PNG.',
    error_save: 'Could not save. Try again.',
    success_saved: 'Profile updated.',
  },
  account_delete: {
    title: 'Delete my account',
    step1_warning_heading: 'Irreversible action',
    step1_warning_body:
      'All your data (orders, loyalty points, profile, receipts) will be deleted within 30 days. You will be signed out immediately.',
    step1_acknowledge: 'I confirm I understand.',
    step1_continue: 'Continue',
    step2_heading: 'Final confirmation',
    step2_body: 'Type DELETE to confirm permanent removal of your account.',
    step2_token_label: 'Confirmation word',
    step2_token_placeholder: 'DELETE',
    step2_token_required: 'DELETE',
    step2_submit: 'Permanently delete',
    deleting: 'Deleting…',
    error: 'Deletion failed. Try again or contact support.',
    success_toast: 'Account deleted. Goodbye 👋',
  },
  preferences: {
    title: 'Preferences',
    language_label: 'Language',
    theme_label: 'Theme',
    theme_auto: 'Auto',
    theme_light: 'Light',
    theme_dark: 'Dark',
    currency_label: 'Currency',
    currency_v2_disabled: 'Available in V2',
  },
  privacy: {
    title: 'Privacy',
    intro:
      'You decide what TapNpay can do with your data. These choices comply with CNDP and GDPR; you can change them anytime.',
    consent_marketing: 'Marketing communications',
    consent_marketing_desc:
      'Receive promos and updates from this restaurant by email or SMS.',
    consent_analytics: 'Anonymous analytics',
    consent_analytics_desc:
      'Help us improve the service via aggregated stats (no identification).',
    consent_video_surveillance: 'Video surveillance',
    consent_video_surveillance_desc:
      "If the restaurant films the dining area for security (CNDP). No personal targeting.",
    consent_third_party: 'Trusted partners',
    consent_third_party_desc:
      'Share technical data with our service providers (payments, hosting).',
    saving: 'Saving…',
    saved: 'Preferences saved.',
    error_save: 'Could not save consent. Try again.',
  },
  payment_methods: {
    title: 'Saved cards',
    intro: 'The cards you chose to remember for your future payments.',
    empty_title: 'No saved cards',
    empty_body:
      'On your next payment, tick "Remember this card" to find it here.',
    remove: 'Remove',
    remove_confirm: 'Remove this card?',
    error_load: 'Cards could not be loaded. Try again.',
    error_remove: 'The card could not be removed. Try again.',
  },
  support: {
    title: 'Support',
    tab_help: 'Help',
    tab_contact: 'Contact',
    tab_about: 'About',
    help_intro: 'The most frequently asked questions.',
    help_q1: 'How do I pay my bill?',
    help_a1:
      'Scan the QR code on your table with your camera. You land on the menu, add to cart, then pay with Apple Pay, Google Pay, card or ZAZU.',
    help_q2: 'Is my data protected?',
    help_a2:
      'TapNpay complies with GDPR and CNDP. Your data is encrypted (AES-256) and you can delete it anytime from Account → Delete my account.',
    help_q3: 'How do I split a bill?',
    help_a3:
      'In the cart, tap "Split the bill". Three modes: equal shares, by item, or custom amount.',
    help_q4: 'How do I add a tip?',
    help_a4:
      'In the cart, click "Add Tip" to open the modal. Choose 0/5/10/15% or a custom amount.',
    help_q5: 'What happens if my network drops mid-payment?',
    help_a5:
      'Your cart and payment session are saved locally. Reconnect and continue where you left off.',
    contact_subject_label: 'Subject',
    contact_subject_placeholder: 'What can we help you with?',
    contact_message_label: 'Message',
    contact_message_placeholder: 'Describe your request in a few lines…',
    contact_email_label: 'Reply email',
    contact_email_placeholder: 'you@example.com',
    contact_send: 'Send',
    contact_sending: 'Sending…',
    contact_success: 'Thanks, we\'ll reply within 48 h.',
    contact_error:
      'Sending failed. You can email us directly at support@tapnpay.ma.',
    contact_email_invalid: 'Invalid email.',
    contact_message_required: 'Describe your request before sending.',
    about_version: 'TapNpay PWA · v{{version}}',
    about_legal_privacy: 'Privacy policy',
    about_legal_terms: 'Terms of service',
    about_legal_cgu: 'EULA',
    about_copyright: '© {{year}} SecurePOS · TapNpay',
  },
  loyalty_actions: {
    share_title: 'TapNpay loyalty programme',
    share_text:
      'Discover TapNpay and earn loyalty points on every bill. Scan, pay, enjoy.',
    share_clipboard_copied: 'Link copied.',
    share_unavailable: 'Sharing unavailable. Link copied to clipboard.',
    invite_title: 'Invite a friend to TapNpay',
    invite_text:
      "Join TapNpay with my code and we both earn points on your first bill.",
    invite_clipboard_copied: 'Invite link copied.',
    how_to_earn_title: 'How to earn points',
    how_to_earn_intro:
      'Your TapNpay points accumulate automatically on every confirmed payment. 1 point = 0.10 MAD in rewards (indicative rate, may vary per venue).',
    how_to_earn_rule1:
      '1 MAD paid via TapNpay = 1 loyalty point credited after payment is confirmed.',
    how_to_earn_rule2:
      "An invited friend's first bill = +50 bonus points (for both of you).",
    how_to_earn_rule3:
      'Google review with 4★ or more after payment = +25 points (once per venue).',
    promo_title: 'Promo code',
    promo_intro: 'Enter a promo code to redeem a discount or bonus points.',
    promo_code_label: 'Code',
    promo_code_placeholder: 'PROMO2026',
    promo_apply: 'Apply',
    promo_applying: 'Applying…',
    promo_success: 'Code applied.',
    promo_error_format: 'Invalid format. 4 to 16 letters or digits.',
    promo_error_invalid: 'Invalid or expired code.',
    promo_error_generic: 'Could not apply. Try again.',
    terms_title: 'Programme terms',
    terms_body:
      'The TapNpay loyalty programme is offered by each partner restaurant. Points accumulate without expiry as long as your account is active. The restaurant may modify or end the programme at any time; you will be notified at least 30 days in advance. Points have no monetary value and are not transferable. Fraud (generated codes, multi-accounts, etc.) triggers account revocation. Full policy at tapnpay.ma/legal/loyalty.',
  },
  loyalty_tier: {
    program_label: 'Loyalty programme',
    member_label: '{{tier}} member',
    next_in: '{{nextTier}} tier in {{delta}} pts',
    max_reached: 'Max tier reached',
    equivalent_in_mad: '≈ {{amount}} MAD in rewards',
  },
  cart: {
    title: 'Cart',
    empty: 'Your cart is empty.',
    browse_menu: 'Browse menu',
    item_count_one: '{{count}} item',
    item_count_other: '{{count}} items',
    proceed_checkout: 'Proceed to checkout',
    summary_subtotal: 'Subtotal',
    summary_vat: 'VAT (20%)',
    summary_tip: 'Tip',
    summary_total: 'Total',
    clear_all: 'Clear all',
  },
  checkout: {
    title: 'Checkout',
    paying: 'Processing payment…',
    method_card: 'Card',
    method_apple_pay: 'Apple Pay',
    method_google_pay: 'Google Pay',
    method_zazu: 'ZAZU',
    method_cash: 'Cash',
    method_cash_subtitle: 'Your server will collect at your table',
    submit: 'Pay {{amount}}',
    success: 'Thanks! Payment confirmed.',
    failure: 'Payment failed.',
    cash_disabled: 'Cash payment is not accepted in this restaurant.',
  },
  cash_wait: {
    title: 'Call your server',
    subtitle: 'Show this screen to your server to pay in cash.',
    order_number_label: 'Order #',
    amount_to_pay: 'Amount to pay',
    instructions: 'Your order has been sent to the kitchen. The server will come to your table to collect payment.',
    status_awaiting: 'Awaiting cash collection…',
    status_received: 'Payment received',
    status_received_body: 'Thanks! Your fiscal receipt is being issued.',
    error_title: 'Error',
    error_body: 'Cannot verify status. Ask your server for confirmation.',
    call_waiter_button: 'Call server',
    back_to_menu: 'Back to menu',
  },
  split: {
    title: 'Split the bill',
    mode_equal: 'Equal shares',
    mode_custom: 'Custom amount',
    mode_by_item: 'By item',
    payers_count: 'Number of payers',
    your_share: 'Your share',
    share_link_title: 'Invite another payer',
    share_link_subtitle:
      'Send this link to whoever pays the rest. It expires with your session.',
    share_link_copy: 'Copy link',
    share_link_copied: 'Link copied.',
    share_link_native: 'Share…',
    share_link_unavailable: 'The link will be available after you confirm.',
    equal_title: 'Equal shares',
    equal_people_label: 'Number of people',
    equal_total_label: 'Total',
    equal_you_pay: 'You pay',
    custom_title: 'Custom amount',
    custom_prompt: 'How much do you want to pay?',
    custom_total_order: 'Order total',
    custom_remaining: 'Left for others',
    by_item_total: 'Total',
    by_item_info: 'Tick the items you are paying for. The rest will be settled by the other guests.',
    by_item_my_items: 'My items',
  },
  confirmation: {
    title: 'Order confirmed',
    subtitle: 'The restaurant has received your order.',
    receipt_email_label: 'Receipt sent to',
    leave_review: 'Leave a review',
    new_order: 'New order',
  },
  language: {
    label: 'Language',
    fr: 'Français',
    en: 'English',
    ar: 'العربية',
  },
  menu: {
    unavailable: 'Menu unavailable',
    all_menu: 'All menu',
    no_items: 'No items in this category.',
    your_addition: 'Your bill',
    nfc_welcome: 'Welcome to {{store}}',
    fallback_store_name: 'TapNpay',
    aria_back: 'Back',
    aria_cart: 'Cart',
    aria_add: 'Add',
    contains_allergen: 'Contains {{allergen}}',
    search_placeholder: 'Search a dish…',
    featured_badge: 'POPULAR',
  },
  bottom_nav: {
    aria_label: 'Main navigation',
    menu: 'Menu',
    scan: 'Scan',
    orders: 'Orders',
    account: 'Account',
  },
  profile: {
    title: 'Account',
    aria_back: 'Back',
    aria_settings: 'Settings',
    aria_edit: 'Edit',
    aria_avatar: 'Avatar',
    not_signed_in_title: 'Not signed in',
    not_signed_in_body:
      'Sign in to manage your profile, cards, and preferences.',
    sign_in: 'Sign in',
    user_fallback: 'User',
    empty_value: '—',
    section_preferences: 'Preferences',
    section_notifications: 'Notifications',
    section_payment: 'Payment',
    section_account: 'Account',
    section_support: 'Support',
    pref_language: 'Language',
    pref_theme: 'Theme',
    pref_currency: 'Currency',
    pref_theme_auto: 'Auto',
    pref_currency_value: 'MAD',
    notif_email_receipts: 'Email receipts',
    notif_push: 'Push notifications',
    notif_sms: 'SMS confirmations',
    payment_cards: 'Saved cards',
    payment_cards_count_zero: '0 cards',
    payment_receipts: 'Payment receipts',
    account_edit: 'Edit profile',
    account_privacy: 'Privacy',
    account_delete: 'Delete my account',
    support_help: 'Help & FAQ',
    support_contact: 'Contact TapNpay',
    support_about: 'About · v1.0.0',
    stat_orders: 'Orders',
    stat_points: 'Points',
    log_out: 'Sign out',
    logging_out: 'Signing out…',
  },
  loyalty: {
    title: 'My Points',
    aria_back: 'Back',
    aria_share: 'Share',
    points_label: 'TapNpay points',
    sign_in_title: 'Sign in to see your points',
    sign_in_cta: 'Sign in',
    action_how_to_earn: 'How to earn?',
    action_promo_codes: 'Promo codes',
    action_invite_friend: 'Invite a friend',
    select_store_empty: 'Select a restaurant to see its rewards.',
    rewards_loading: 'Loading rewards…',
    rewards_empty: 'The loyalty programme will be available soon.',
    recent_activity: 'Recent activity',
    activity_loading: 'Loading…',
    activity_empty: 'No activity yet.',
    activity_fallback_label: 'Activity',
    terms_cta: 'Programme terms',
    points_unit: 'pts',
    empty_value: '—',
  },
  reservation: {
    title: 'Reservations',
    aria_back: 'Back',
    not_found_title: 'Restaurant not found',
    not_found_body: 'Check the link or go back home.',
    not_found_cta: 'Back home',
    loading: 'Loading…',
    date_label: 'Date',
    time_label: 'Time',
    slots_loading: 'Loading…',
    slots_error: 'Could not load time slots. Try again in a moment.',
    slots_empty: 'No slots available on that day.',
    party_size_label: 'Number of guests',
    guests_label: 'Guests',
    aria_decrement: 'Decrease',
    aria_increment: 'Increase',
    contact_label: 'Contact details',
    full_name_label: 'Full name',
    full_name_placeholder: 'Your name',
    phone_label: 'Phone',
    email_label: 'Email (optional)',
    email_placeholder: 'you@example.com',
    request_label: 'Special request (optional)',
    request_placeholder: 'Birthday, allergy, terrace table…',
    submit_cta: 'Confirm reservation',
    submitting: 'Confirming…',
    footer_cancellation: 'Free cancellation up to 2 h before the reservation.',
    error_generic: 'Submission error',
    success_title: 'Reservation confirmed',
    success_body: 'A confirmation email is on its way.',
    success_restaurant_label: 'Restaurant',
    success_date_label: 'Date',
    success_time_label: 'Time',
    success_guests_label: 'Guests',
    success_name_label: 'On behalf of',
    success_calendar_cta: 'Add to calendar',
    success_my_reservations_cta: 'My reservations',
    calendar_event_title: 'Reservation · {{store}}',
    calendar_event_details:
      'TapNpay reservation under {{name}} for {{count}} guest(s).',
    weekday_sun: 'Sun',
    weekday_mon: 'Mon',
    weekday_tue: 'Tue',
    weekday_wed: 'Wed',
    weekday_thu: 'Thu',
    weekday_fri: 'Fri',
    weekday_sat: 'Sat',
  },
  nps: {
    aria_skip: 'Skip survey',
    header_brand: 'TAPNPAY',
    prompt_initial: 'On a scale of 0 to 10, would you recommend TapNpay to a friend?',
    prompt_after: 'Your recommendation score',
    subtitle: 'Your feedback helps us improve.',
    scale_low: 'Not at all',
    scale_mid: 'Neutral',
    scale_high: 'Absolutely',
    aria_score: 'Score {{n}}',
    detractor_title: 'What disappointed you?',
    passive_title: 'What could have been better?',
    promoter_title: 'Awesome! What did you love?',
    detractor_reason_slow: 'App was slow',
    detractor_reason_hard: 'Hard to use',
    detractor_reason_payment: 'Payment problem',
    detractor_reason_glitches: 'UI glitches',
    detractor_reason_other: 'Other',
    promoter_reason_fast: 'Fast',
    promoter_reason_simple: 'Simple',
    promoter_reason_convenient: 'Convenient',
    promoter_reason_no_download: 'No download',
    promoter_reason_secure: 'Secure',
    promoter_reason_other: 'Other',
    details_label: 'Details (optional)',
    details_placeholder_detractor: 'Tell us more about your experience…',
    details_placeholder_passive: 'Optional — your feedback helps us improve…',
    google_review_prompt: 'Help us by leaving a Google review?',
    google_review_cta: 'Leave a review ★',
    google_review_unavailable: 'Review unavailable',
    submit_cta: 'Submit feedback',
    submitting: 'Submitting…',
    thank_you_title: 'Thank you for your feedback ✓',
    thank_you_subtitle: 'Your review has been recorded.',
    returning_home: 'Returning home in 3 s…',
    footer_brand: 'TAPNPAY SECURE TERMINAL',
  },
  order_history: {
    title: 'Order history',
    aria_back: 'Back',
    filter_all: 'All',
    filter_paid: 'Paid',
    filter_cancelled: 'Cancelled',
    empty_title: 'No past orders',
    empty_body: 'Orders you place via TapNpay will appear here.',
    total_label: 'Total',
    reorder_cta: 'Re-order',
    status_submitted: 'Submitted',
    status_preparing: 'Preparing',
    status_ready: 'Ready',
    status_served: 'Served',
    status_paid: 'Paid',
    status_cancelled: 'Cancelled',
  },
  my_orders: {
    title: 'My orders',
    aria_back: 'Back',
    empty_title: 'No orders yet',
    empty_body: 'Your orders will appear here after payment.',
    order_fallback: 'Order',
    total_label: 'Total',
    request_call_waiter: 'Call waiter',
    request_bill: 'Request bill',
    cta_order_more: 'Order more',
    toast_waiter_notified: 'Waiter notified.',
    toast_bill_requested: 'Bill request sent.',
    status_pending: 'Pending',
    status_confirmed: 'Confirmed',
    status_preparing: 'Preparing',
    status_ready: 'Ready',
    status_delivered: 'Delivered',
    status_cancelled: 'Cancelled',
  },
  call_waiter: {
    fab_label: 'Call waiter',
    sending: 'Sending…',
    sent: 'Waiter notified ✓',
    cooldown: 'Request sent ✓',
    error: 'Failed — try again shortly.',
    dedup_short: 'A request was just sent. Hold on a moment.',
    rate_limited: 'Too many requests — try again in a minute.',
  },
  nfc_waiting: {
    unsupported_title: 'NFC not supported',
    unsupported_body: 'Use the QR code below instead.',
    denied_title: 'Permission denied',
    denied_body: 'NFC permission denied.',
    error_title: 'NFC error',
    error_body: 'NFC read failed.',
    timeout_title: 'No tag detected',
    timeout_body: '30 s without detection.',
    ready_title: 'Hold your phone near',
    ready_body: 'the NFC sticker',
    time_remaining: '30 s remaining',
    use_qr_cta: 'USE QR CODE INSTEAD',
    retry_nfc: 'Retry NFC',
    cancel: 'Cancel',
  },
  resolve_table: {
    welcome_fallback: 'Welcome',
    opening_menu: 'Opening menu…',
    ready_to_order: 'Ready to order ✓',
    not_found_title: 'Table not found',
    not_found_body: 'Ask a server or rescan the QR code.',
    rescan_cta: 'Rescan',
    call_server_cta: 'Call a server',
    powered_by: 'Powered by TapNpay',
  },
  geofence: {
    requesting: 'Checking your location…',
    permission_title: 'Enable location access',
    permission_body: 'To pay at your table, allow access to your location.',
    permission_cta: 'Allow',
    outside_title: 'You appear to be outside the restaurant',
    outside_body: 'Ask a server to take your payment.',
    outside_call_waiter: 'Call a server',
    outside_retry: 'Try again',
    unsupported_title: 'Location unavailable',
    unsupported_body: 'Your browser does not support geolocation. Ask a server to complete your order.',
    inconsistent_title: 'Imprecise location',
    inconsistent_body: 'Your GPS is not accurate enough. Step outside and try again, or ask a server.',
    verified_badge: 'Verified within {{distance}} m',
  },
  zazu_verify: {
    success_title: 'Payment confirmed',
    success_body: 'Your payment was received.',
    pending_title: 'Verifying payment',
    pending_body: 'Your payment may take a few seconds to confirm. You can refresh or check your orders.',
    pending_cta_retry: 'Verify now',
    pending_cta_orders: 'View my orders',
    canceled_title: 'Payment canceled',
    canceled_body: 'Your cart is saved.',
    canceled_cta_back: 'Back to cart',
    failed_title: 'Payment failed',
    failed_body: 'An error occurred. Please try again.',
    failed_cta_retry: 'Try again',
    error_title: 'Verification unavailable',
    error_body: 'No network connection. Please try again in a moment.',
    error_cta_retry: 'Try again',
  },
  qr_bill: {
    error_missing_token: 'Bill link missing.',
    error_expired: 'This bill has expired.',
    error_unavailable: 'Bill {{status}}.',
    error_payment_init: 'Payment initialization failed.',
    error_no_payment_url: 'No payment URL returned.',
    bill_unavailable_title: 'Bill unavailable',
    back_cta: 'Back',
    item_fallback: 'Item',
    subtotal: 'Subtotal',
    tax: 'Tax',
    tip_legend: 'Tip (optional)',
    tip_custom: 'Custom',
    tip_custom_placeholder: 'Tip in {{currency}}',
    contact_legend: 'Email or phone (for fiscal receipt)',
    contact_email_label: 'Email',
    contact_sms_label: 'SMS',
    email_placeholder: 'you@example.com',
    phone_placeholder: '+212 6XX XXX XXX',
    total_label: 'Total',
    pay_cta: 'Pay {{amount}}',
    pay_opening: 'Opening payment…',
    pay_methods: 'Apple Pay · Google Pay · Card via ZAZU',
    confirming_title: 'Confirming payment…',
    confirming_body: 'This usually takes a few seconds.',
    rating_title: 'Thanks for paying',
    rating_body: 'How was your experience?',
    rating_low_placeholder: 'What didn\'t go well? (optional)',
    rating_send_feedback: 'Send feedback',
    rating_skip: 'No thanks',
    done_title: 'All set',
    done_body: 'Your fiscal receipt is on its way.',
  },
  review_prompt: {
    aria_stars: '5 out of 5 stars rating',
    title: 'How was your experience?',
    body_with_store: 'Help {{store}} by sharing your review.',
    body_generic: 'Share your experience.',
    review_google: 'Review on Google',
    review_unconfigured: 'Reviews not yet configured',
    review_tripadvisor: 'Review on TripAdvisor',
    coming_soon: '(Coming soon)',
    skip_cta: 'Maybe later',
  },
  gift_card_purchase: {
    aria_back: 'Back',
    title: 'Gift card',
    hero_title: 'Give a gift card',
    hero_valid_at: 'Valid at {{store}}',
    section_amount: 'Amount',
    custom_amount_label: 'Custom amount',
    recipient_toggle: 'Is it for someone else?',
    recipient_email_label: 'Recipient email',
    recipient_email_placeholder: 'email@example.com',
    message_label: 'Message (optional)',
    message_placeholder: 'Add a personal note…',
    error_invalid_form: 'Check the amount and the recipient email.',
    error_no_session: 'Payment session unavailable.',
    cta_buy: 'Buy',
    footer_email_recipient: 'The recipient will receive the code by email.',
    payment_title: 'Payment',
    payment_total: 'Total',
    payment_processing: 'Processing…',
    payment_pay_cta: 'Pay — {{amount}}',
    payment_failed: 'Payment failed.',
  },
  gift_card_redeem: {
    aria_back: 'Back',
    title: 'Gift card',
    hero_balance_label: 'Remaining balance',
    hero_default_code: 'TPN-XXX-XXX',
    code_label: 'Your card code',
    code_placeholder: 'TPN-XXXX-YYYY',
    code_email_hint: 'You can find the code in the email you received.',
    code_invalid: 'Invalid or expired code.',
    expires_prefix: 'Expires {{date}}',
    status_active: 'Valid ✓',
    status_redeemed: 'Already used',
    status_expired: 'Expired',
    status_cancelled: 'Cancelled',
    history_title: 'History',
    history_empty: 'No usage yet.',
    error_no_store: 'Scan a table QR first to use this card.',
    error_empty_cart: 'Your cart is empty.',
    error_invalid: 'Invalid code',
    cta_apply: 'Apply to this bill',
    cta_applying: 'Applying…',
    cta_applied: 'Applied — see the cart',
    footer_or_at_checkout: 'Or add this code at checkout.',
  },
  waiter_status: {
    requested: 'Request sent',
    ack: 'Received by server',
    en_route: 'On the way',
    resolved: 'Served',
  },
  order_tracking: {
    title: 'Order tracking',
    item_pending: 'Pending',
    item_preparing: 'Preparing',
    item_ready: 'Ready',
    item_served: 'Served',
  },
  tip_chips: {
    title: 'Add a tip?',
    custom: 'Custom',
    suggested: 'Suggested',
  },
  item_notes: {
    placeholder: 'Special notes (e.g. no onion)',
    label: 'Special notes',
  },
  recommendations: {
    title: 'Often ordered with',
  },
  whatsapp_share: {
    btn: 'Share via WhatsApp',
    message: 'Here is my share: {{amount}} MAD — {{link}}',
  },
  order_ahead: {
    title: 'Order ahead',
    pickup_time: 'Pickup time',
    name: 'Your name',
    phone: 'Phone',
    btn_submit: 'Confirm order',
    confirmation: 'Order confirmed for {{time}}',
  },
  stock_badge: {
    low_stock: 'Only {{n}} left',
  },
  promotions_client: {
    label: 'PROMO -{{pct}}%',
  },
};

const ar: Messages = {
  common: {
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    back: 'رجوع',
    continue: 'متابعة',
    loading: 'جارٍ التحميل…',
    retry: 'إعادة المحاولة',
    error_generic: 'حدث خطأ. حاول مرة أخرى.',
    total: 'المجموع',
    subtotal: 'المجموع الفرعي',
    tax: 'الضريبة',
    tip: 'الإكرامية',
    yes: 'نعم',
    no: 'لا',
    save: 'حفظ',
    pay: 'الدفع',
    share: 'مشاركة',
    close: 'إغلاق',
    edit: 'تعديل',
    delete: 'حذف',
    saving: 'جارٍ الحفظ…',
    saved: 'تم الحفظ.',
  },
  profile_edit: {
    title: 'تعديل ملفي الشخصي',
    full_name_label: 'الاسم الكامل',
    full_name_placeholder: 'اسمك',
    phone_label: 'الهاتف',
    phone_placeholder: '600 000 000',
    phone_country_prefix: '+212',
    avatar_label: 'صورة الملف الشخصي',
    avatar_choose: 'اختر صورة',
    avatar_remove: 'إزالة',
    avatar_uploading: 'جارٍ الرفع…',
    error_phone_format: 'رقم غير صالح. الصيغة المتوقعة: 6XXXXXXXX أو +212 6XXXXXXXX.',
    error_avatar_too_large: 'الصورة كبيرة جدًا (الحد الأقصى 2 ميغا).',
    error_avatar_type: 'صيغة غير مدعومة. استخدم JPG أو PNG.',
    error_save: 'تعذّر الحفظ. حاول مرة أخرى.',
    success_saved: 'تم تحديث الملف الشخصي.',
  },
  account_delete: {
    title: 'حذف حسابي',
    step1_warning_heading: 'إجراء لا رجعة فيه',
    step1_warning_body:
      'سيتم حذف جميع بياناتك (الطلبات، نقاط الولاء، الملف الشخصي، الإيصالات) خلال 30 يومًا. سيتم تسجيل خروجك فورًا.',
    step1_acknowledge: 'أؤكد أنني فهمت.',
    step1_continue: 'متابعة',
    step2_heading: 'التأكيد النهائي',
    step2_body: 'اكتب DELETE لتأكيد الحذف النهائي لحسابك.',
    step2_token_label: 'كلمة التأكيد',
    step2_token_placeholder: 'DELETE',
    step2_token_required: 'DELETE',
    step2_submit: 'حذف نهائي',
    deleting: 'جارٍ الحذف…',
    error: 'فشل الحذف. حاول مرة أخرى أو تواصل مع الدعم.',
    success_toast: 'تم حذف الحساب. وداعًا 👋',
  },
  preferences: {
    title: 'التفضيلات',
    language_label: 'اللغة',
    theme_label: 'السمة',
    theme_auto: 'تلقائي',
    theme_light: 'فاتح',
    theme_dark: 'داكن',
    currency_label: 'العملة',
    currency_v2_disabled: 'متوفر في الإصدار 2',
  },
  privacy: {
    title: 'الخصوصية',
    intro:
      'أنت من يقرر ماذا يمكن لـ TapNpay فعله ببياناتك. هذه الخيارات متوافقة مع CNDP و GDPR، ويمكنك تعديلها في أي وقت.',
    consent_marketing: 'الاتصالات التسويقية',
    consent_marketing_desc:
      'استلم العروض والأخبار من المطعم عبر البريد الإلكتروني أو SMS.',
    consent_analytics: 'تحليلات مجهولة',
    consent_analytics_desc:
      'ساعدنا على تحسين الخدمة عبر إحصائيات مجمّعة (دون أي تعريف).',
    consent_video_surveillance: 'المراقبة بالفيديو',
    consent_video_surveillance_desc:
      'إذا كان المطعم يصوّر القاعة للأمان (CNDP). لا استهداف شخصي.',
    consent_third_party: 'شركاء موثوقون',
    consent_third_party_desc:
      'مشاركة بيانات تقنية مع مزوّدي خدمتنا (الدفع، الاستضافة).',
    saving: 'جارٍ الحفظ…',
    saved: 'تم حفظ التفضيلات.',
    error_save: 'تعذّر حفظ الموافقة. حاول مرة أخرى.',
  },
  payment_methods: {
    title: 'البطاقات المحفوظة',
    intro: 'البطاقات التي اخترت تذكّرها لمدفوعاتك المستقبلية.',
    empty_title: 'لا توجد بطاقات محفوظة',
    empty_body: 'في الدفعة القادمة، أشّر على «احفظ هذه البطاقة» لتجدها هنا.',
    remove: 'إزالة',
    remove_confirm: 'إزالة هذه البطاقة؟',
    error_load: 'تعذّر تحميل البطاقات. حاول مرة أخرى.',
    error_remove: 'تعذّر إزالة البطاقة. حاول مرة أخرى.',
  },
  support: {
    title: 'الدعم',
    tab_help: 'مساعدة',
    tab_contact: 'تواصل',
    tab_about: 'حول',
    help_intro: 'الأسئلة الأكثر شيوعًا.',
    help_q1: 'كيف أدفع فاتورتي؟',
    help_a1:
      'امسح رمز QR على طاولتك بالكاميرا. تصل إلى القائمة، تضيف إلى السلة ثم تدفع عبر Apple Pay أو Google Pay أو بطاقة أو ZAZU.',
    help_q2: 'هل بياناتي محمية؟',
    help_a2:
      'TapNpay متوافق مع GDPR وCNDP. بياناتك مشفّرة (AES-256) ويمكنك حذفها في أي وقت من الحساب ← حذف حسابي.',
    help_q3: 'كيف أقسّم الفاتورة؟',
    help_a3:
      'في السلة، اختر «تقسيم الفاتورة». ثلاث طرق: حصص متساوية، حسب الطبق، أو مبلغ مخصص.',
    help_q4: 'كيف أضيف إكرامية؟',
    help_a4:
      'في السلة، اضغط «إكرامية» لفتح النافذة. اختر 0/5/10/15% أو مبلغًا مخصصًا.',
    help_q5: 'ماذا يحدث إذا انقطعت الشبكة أثناء الدفع؟',
    help_a5:
      'يتم حفظ سلتك وجلسة الدفع محليًا. أعد الاتصال بالإنترنت وتابع من حيث توقفت.',
    contact_subject_label: 'الموضوع',
    contact_subject_placeholder: 'بماذا يمكننا مساعدتك؟',
    contact_message_label: 'الرسالة',
    contact_message_placeholder: 'صف طلبك في بضعة أسطر…',
    contact_email_label: 'البريد الإلكتروني للرد',
    contact_email_placeholder: 'you@example.com',
    contact_send: 'إرسال',
    contact_sending: 'جارٍ الإرسال…',
    contact_success: 'شكرًا، سنرد خلال 48 ساعة.',
    contact_error:
      'فشل الإرسال. يمكنك مراسلتنا مباشرة عبر support@tapnpay.ma.',
    contact_email_invalid: 'بريد إلكتروني غير صالح.',
    contact_message_required: 'صف طلبك قبل الإرسال.',
    about_version: 'تطبيق TapNpay · v{{version}}',
    about_legal_privacy: 'سياسة الخصوصية',
    about_legal_terms: 'الشروط العامة',
    about_legal_cgu: 'CGU',
    about_copyright: '© {{year}} SecurePOS · TapNpay',
  },
  loyalty_actions: {
    share_title: 'برنامج ولاء TapNpay',
    share_text: 'اكتشف TapNpay واكسب نقاط الولاء مع كل فاتورة. امسح، ادفع، استمتع.',
    share_clipboard_copied: 'تم نسخ الرابط.',
    share_unavailable: 'المشاركة غير متاحة. تم نسخ الرابط في الحافظة.',
    invite_title: 'ادعُ صديقًا إلى TapNpay',
    invite_text: 'انضم إلى TapNpay برمزي ونكسب معًا نقاطًا في فاتورتك الأولى.',
    invite_clipboard_copied: 'تم نسخ رابط الدعوة.',
    how_to_earn_title: 'كيفية كسب النقاط',
    how_to_earn_intro:
      'تتراكم نقاط TapNpay تلقائيًا مع كل دفعة مؤكدة. 1 نقطة = 0.10 درهم من المكافآت (سعر إرشادي، قد يختلف حسب المحل).',
    how_to_earn_rule1:
      '1 درهم مدفوع عبر TapNpay = 1 نقطة ولاء بعد تأكيد الدفع.',
    how_to_earn_rule2:
      'أول فاتورة لصديق دعوته = +50 نقطة مكافأة (لكليكما).',
    how_to_earn_rule3:
      'تقييم Google ★★★★ أو أكثر بعد الدفع = +25 نقطة (مرة واحدة لكل محل).',
    promo_title: 'رمز ترويجي',
    promo_intro: 'أدخل رمزًا ترويجيًا للاستفادة من خصم أو نقاط إضافية.',
    promo_code_label: 'الرمز',
    promo_code_placeholder: 'PROMO2026',
    promo_apply: 'تطبيق',
    promo_applying: 'جارٍ التطبيق…',
    promo_success: 'تم تطبيق الرمز.',
    promo_error_format: 'صيغة غير صالحة. من 4 إلى 16 حرفًا أو رقمًا.',
    promo_error_invalid: 'رمز غير صالح أو منتهي الصلاحية.',
    promo_error_generic: 'تعذّر التطبيق. حاول مرة أخرى.',
    terms_title: 'شروط البرنامج',
    terms_body:
      'برنامج ولاء TapNpay يُقدَّم من كل مطعم شريك. تتراكم النقاط دون انتهاء صلاحية ما دام الحساب نشطًا. يمكن للمطعم تعديل البرنامج أو إنهاؤه في أي وقت؛ سيتم إشعارك قبل 30 يومًا على الأقل. النقاط ليس لها قيمة نقدية ولا يمكن نقلها. أي احتيال (رموز مولّدة، حسابات متعددة، إلخ) يؤدي إلى إلغاء الحساب. السياسة الكاملة على tapnpay.ma/legal/loyalty.',
  },
  loyalty_tier: {
    program_label: 'برنامج الولاء',
    member_label: 'عضو {{tier}}',
    next_in: 'مستوى {{nextTier}} بعد {{delta}} نقطة',
    max_reached: 'تم بلوغ أعلى مستوى',
    equivalent_in_mad: '≈ {{amount}} درهم كمكافآت',
  },
  cart: {
    title: 'السلة',
    empty: 'سلتك فارغة.',
    browse_menu: 'تصفح القائمة',
    item_count_one: '{{count}} عنصر',
    item_count_other: '{{count}} عناصر',
    proceed_checkout: 'المتابعة إلى الدفع',
    summary_subtotal: 'المجموع الفرعي',
    summary_vat: 'الضريبة على القيمة المضافة (20%)',
    summary_tip: 'البقشيش',
    summary_total: 'الإجمالي',
    clear_all: 'إفراغ السلة',
  },
  checkout: {
    title: 'الدفع',
    paying: 'جارٍ الدفع…',
    method_card: 'بطاقة',
    method_apple_pay: 'Apple Pay',
    method_google_pay: 'Google Pay',
    method_zazu: 'ZAZU',
    method_cash: 'نقدًا',
    method_cash_subtitle: 'سيقوم النادل بتحصيل المبلغ عند طاولتك',
    submit: 'ادفع {{amount}}',
    success: 'شكرًا لك! تم تأكيد الدفع.',
    failure: 'فشلت عملية الدفع.',
    cash_disabled: 'الدفع نقدًا غير مقبول في هذا المطعم.',
  },
  cash_wait: {
    title: 'نادِ النادل',
    subtitle: 'أرِ هذه الشاشة لنادلك للدفع نقدًا.',
    order_number_label: 'طلب رقم',
    amount_to_pay: 'المبلغ المطلوب دفعه',
    instructions: 'تم إرسال طلبك إلى المطبخ. سيأتي النادل إلى طاولتك لتحصيل المبلغ.',
    status_awaiting: 'في انتظار التحصيل…',
    status_received: 'تم استلام المبلغ',
    status_received_body: 'شكرًا! جارٍ إصدار إيصالك الضريبي.',
    error_title: 'خطأ',
    error_body: 'تعذّر التحقق من الحالة. اطلب التأكيد من النادل.',
    call_waiter_button: 'نادِ النادل',
    back_to_menu: 'العودة إلى القائمة',
  },
  split: {
    title: 'تقسيم الفاتورة',
    mode_equal: 'حصص متساوية',
    mode_custom: 'مبلغ مخصص',
    mode_by_item: 'حسب الطبق',
    payers_count: 'عدد الدافعين',
    your_share: 'حصتك',
    share_link_title: 'دعوة دافع آخر',
    share_link_subtitle:
      'أرسل هذا الرابط إلى من سيدفع الباقي. ينتهي مع انتهاء جلستك.',
    share_link_copy: 'نسخ الرابط',
    share_link_copied: 'تم نسخ الرابط.',
    share_link_native: 'مشاركة…',
    share_link_unavailable: 'سيتوفر الرابط بعد التأكيد.',
    equal_title: 'حصص متساوية',
    equal_people_label: 'عدد الأشخاص',
    equal_total_label: 'الإجمالي',
    equal_you_pay: 'حصتك',
    custom_title: 'مبلغ مخصص',
    custom_prompt: 'كم تريد أن تدفع؟',
    custom_total_order: 'إجمالي الطلب',
    custom_remaining: 'المتبقي للآخرين',
    by_item_total: 'الإجمالي',
    by_item_info: 'صيّب على الأصناف اللي غادي تخلّص. الباقي غادي يخلّصوه الضيوف الآخرين.',
    by_item_my_items: 'أصنافي',
  },
  confirmation: {
    title: 'تم تأكيد الطلب',
    subtitle: 'استلم المطعم طلبك.',
    receipt_email_label: 'الإيصال مرسل إلى',
    leave_review: 'اترك تقييمًا',
    new_order: 'طلب جديد',
  },
  language: {
    label: 'اللغة',
    fr: 'Français',
    en: 'English',
    ar: 'العربية',
  },
  menu: {
    unavailable: 'القائمة غير متاحة',
    all_menu: 'القائمة كاملة',
    no_items: 'لا توجد عناصر في هذه الفئة.',
    your_addition: 'فاتورتك',
    nfc_welcome: 'أهلاً بك في {{store}}',
    fallback_store_name: 'TapNpay',
    aria_back: 'رجوع',
    aria_cart: 'السلة',
    aria_add: 'إضافة',
    contains_allergen: 'يحتوي على {{allergen}}',
    search_placeholder: 'ابحث عن طبق…',
    featured_badge: 'الأكثر طلباً',
  },
  bottom_nav: {
    aria_label: 'التنقل الرئيسي',
    menu: 'القائمة',
    scan: 'مسح',
    orders: 'الطلبات',
    account: 'الحساب',
  },
  profile: {
    title: 'الحساب',
    aria_back: 'رجوع',
    aria_settings: 'الإعدادات',
    aria_edit: 'تعديل',
    aria_avatar: 'الصورة الرمزية',
    not_signed_in_title: 'غير مسجّل الدخول',
    not_signed_in_body:
      'سجّل الدخول لإدارة ملفك الشخصي وبطاقاتك وتفضيلاتك.',
    sign_in: 'تسجيل الدخول',
    user_fallback: 'مستخدم',
    empty_value: '—',
    section_preferences: 'التفضيلات',
    section_notifications: 'الإشعارات',
    section_payment: 'الدفع',
    section_account: 'الحساب',
    section_support: 'الدعم',
    pref_language: 'اللغة',
    pref_theme: 'السمة',
    pref_currency: 'العملة',
    pref_theme_auto: 'تلقائي',
    pref_currency_value: 'درهم',
    notif_email_receipts: 'إيصالات بالبريد',
    notif_push: 'إشعارات الهاتف',
    notif_sms: 'تأكيد عبر SMS',
    payment_cards: 'البطاقات المحفوظة',
    payment_cards_count_zero: '0 بطاقة',
    payment_receipts: 'إيصالات الدفع',
    account_edit: 'تعديل الملف الشخصي',
    account_privacy: 'الخصوصية',
    account_delete: 'حذف حسابي',
    support_help: 'المساعدة والأسئلة',
    support_contact: 'تواصل مع TapNpay',
    support_about: 'حول · v1.0.0',
    stat_orders: 'الطلبات',
    stat_points: 'النقاط',
    log_out: 'تسجيل الخروج',
    logging_out: 'جارٍ تسجيل الخروج…',
  },
  loyalty: {
    title: 'نقاطي',
    aria_back: 'رجوع',
    aria_share: 'مشاركة',
    points_label: 'نقاط TapNpay',
    sign_in_title: 'سجّل الدخول لرؤية نقاطك',
    sign_in_cta: 'تسجيل الدخول',
    action_how_to_earn: 'كيف تكسب؟',
    action_promo_codes: 'رموز ترويجية',
    action_invite_friend: 'ادعُ صديقًا',
    select_store_empty: 'اختر مطعمًا لعرض مكافآته.',
    rewards_loading: 'جارٍ تحميل المكافآت…',
    rewards_empty: 'سيكون برنامج الولاء متاحًا قريبًا.',
    recent_activity: 'النشاط الأخير',
    activity_loading: 'جارٍ التحميل…',
    activity_empty: 'لا يوجد نشاط حتى الآن.',
    activity_fallback_label: 'نشاط',
    terms_cta: 'شروط البرنامج',
    points_unit: 'نقطة',
    empty_value: '—',
  },
  reservation: {
    title: 'الحجوزات',
    aria_back: 'رجوع',
    not_found_title: 'المطعم غير موجود',
    not_found_body: 'تحقّق من الرابط أو عُد إلى الصفحة الرئيسية.',
    not_found_cta: 'العودة إلى الرئيسية',
    loading: 'جارٍ التحميل…',
    date_label: 'التاريخ',
    time_label: 'الوقت',
    slots_loading: 'جارٍ التحميل…',
    slots_error: 'تعذّر تحميل المواعيد. حاول بعد لحظات.',
    slots_empty: 'لا توجد مواعيد متاحة في هذا اليوم.',
    party_size_label: 'عدد الضيوف',
    guests_label: 'ضيوف',
    aria_decrement: 'إنقاص',
    aria_increment: 'زيادة',
    contact_label: 'بيانات التواصل',
    full_name_label: 'الاسم الكامل',
    full_name_placeholder: 'اسمك',
    phone_label: 'الهاتف',
    email_label: 'البريد الإلكتروني (اختياري)',
    email_placeholder: 'you@example.com',
    request_label: 'طلب خاص (اختياري)',
    request_placeholder: 'عيد ميلاد، حساسية، طاولة في الشرفة…',
    submit_cta: 'تأكيد الحجز',
    submitting: 'جارٍ التأكيد…',
    footer_cancellation: 'إلغاء مجاني حتى ساعتين قبل موعد الحجز.',
    error_generic: 'خطأ في الإرسال',
    success_title: 'تم تأكيد الحجز',
    success_body: 'سيصلك بريد تأكيد قريبًا.',
    success_restaurant_label: 'المطعم',
    success_date_label: 'التاريخ',
    success_time_label: 'الوقت',
    success_guests_label: 'الضيوف',
    success_name_label: 'باسم',
    success_calendar_cta: 'أضف إلى التقويم',
    success_my_reservations_cta: 'حجوزاتي',
    calendar_event_title: 'حجز · {{store}}',
    calendar_event_details:
      'حجز TapNpay باسم {{name}} لـ {{count}} شخص.',
    weekday_sun: 'أحد',
    weekday_mon: 'إثن',
    weekday_tue: 'ثلا',
    weekday_wed: 'أرب',
    weekday_thu: 'خمي',
    weekday_fri: 'جمع',
    weekday_sat: 'سبت',
  },
  nps: {
    aria_skip: 'تخطي الاستبيان',
    header_brand: 'TAPNPAY',
    prompt_initial: 'على مقياس من 0 إلى 10، هل تنصح بـ TapNpay لصديق؟',
    prompt_after: 'نقطة التوصية',
    subtitle: 'رأيك يساعدنا على التحسّن.',
    scale_low: 'إطلاقًا',
    scale_mid: 'محايد',
    scale_high: 'تمامًا',
    aria_score: 'النقطة {{n}}',
    detractor_title: 'ما الذي خيّب أملك؟',
    passive_title: 'ما الذي كان يمكن أن يكون أفضل؟',
    promoter_title: 'رائع! ما الذي أعجبك؟',
    detractor_reason_slow: 'التطبيق كان بطيئًا',
    detractor_reason_hard: 'صعب الاستخدام',
    detractor_reason_payment: 'مشكلة في الدفع',
    detractor_reason_glitches: 'مشاكل في الواجهة',
    detractor_reason_other: 'أخرى',
    promoter_reason_fast: 'سريع',
    promoter_reason_simple: 'بسيط',
    promoter_reason_convenient: 'مريح',
    promoter_reason_no_download: 'بدون تنزيل',
    promoter_reason_secure: 'آمن',
    promoter_reason_other: 'أخرى',
    details_label: 'تفاصيل (اختياري)',
    details_placeholder_detractor: 'أخبرنا أكثر عن تجربتك…',
    details_placeholder_passive: 'اختياري — رأيك يساعدنا على التحسّن…',
    google_review_prompt: 'ساعدنا بترك تقييم على Google؟',
    google_review_cta: 'اترك تقييمًا ★',
    google_review_unavailable: 'التقييم غير متاح',
    submit_cta: 'إرسال التقييم',
    submitting: 'جارٍ الإرسال…',
    thank_you_title: 'شكرًا على ملاحظتك ✓',
    thank_you_subtitle: 'تم تسجيل تقييمك.',
    returning_home: 'العودة إلى الرئيسية خلال 3 ث…',
    footer_brand: 'TAPNPAY SECURE TERMINAL',
  },
  order_history: {
    title: 'سجلّ الطلبات',
    aria_back: 'رجوع',
    filter_all: 'الكل',
    filter_paid: 'المدفوعة',
    filter_cancelled: 'الملغاة',
    empty_title: 'لا توجد طلبات سابقة',
    empty_body: 'ستظهر طلباتك عبر TapNpay هنا.',
    total_label: 'المجموع',
    reorder_cta: 'إعادة الطلب',
    status_submitted: 'مرسل',
    status_preparing: 'قيد التحضير',
    status_ready: 'جاهز',
    status_served: 'تم التقديم',
    status_paid: 'مدفوع',
    status_cancelled: 'ملغى',
  },
  my_orders: {
    title: 'طلباتي',
    aria_back: 'رجوع',
    empty_title: 'لا توجد طلبات بعد',
    empty_body: 'ستظهر طلباتك هنا بعد الدفع.',
    order_fallback: 'طلب',
    total_label: 'المجموع',
    request_call_waiter: 'استدعاء النادل',
    request_bill: 'طلب الفاتورة',
    cta_order_more: 'طلب المزيد',
    toast_waiter_notified: 'تم إشعار النادل.',
    toast_bill_requested: 'تم إرسال طلب الفاتورة.',
    status_pending: 'قيد الانتظار',
    status_confirmed: 'مؤكَّد',
    status_preparing: 'قيد التحضير',
    status_ready: 'جاهز',
    status_delivered: 'تم التقديم',
    status_cancelled: 'ملغى',
  },
  call_waiter: {
    fab_label: 'استدعاء النادل',
    sending: 'جارٍ الإرسال…',
    sent: 'تم إشعار النادل ✓',
    cooldown: 'تم إرسال الطلب ✓',
    error: 'فشل الإرسال — أعد المحاولة بعد قليل.',
    dedup_short: 'تم إرسال طلب للتو. انتظر لحظة من فضلك.',
    rate_limited: 'طلبات كثيرة جدًا — أعد المحاولة بعد دقيقة.',
  },
  nfc_waiting: {
    unsupported_title: 'NFC غير مدعوم',
    unsupported_body: 'استخدم رمز QR بدلاً من ذلك.',
    denied_title: 'تم رفض الإذن',
    denied_body: 'تم رفض إذن NFC.',
    error_title: 'خطأ NFC',
    error_body: 'فشلت قراءة NFC.',
    timeout_title: 'لم يتم العثور على بطاقة',
    timeout_body: '30 ثانية دون اكتشاف.',
    ready_title: 'قرّب هاتفك',
    ready_body: 'من ملصق NFC',
    time_remaining: 'تبقّى 30 ثانية',
    use_qr_cta: 'استخدم رمز QR',
    retry_nfc: 'إعادة محاولة NFC',
    cancel: 'إلغاء',
  },
  resolve_table: {
    welcome_fallback: 'أهلاً بك',
    opening_menu: 'جارٍ فتح القائمة…',
    ready_to_order: 'جاهز للطلب ✓',
    not_found_title: 'الطاولة غير موجودة',
    not_found_body: 'اطلب من النادل، أو أعد مسح رمز QR.',
    rescan_cta: 'إعادة المسح',
    call_server_cta: 'نادِ النادل',
    powered_by: 'مشغَّل بواسطة TapNpay',
  },
  geofence: {
    requesting: 'جارٍ التحقق من موقعك…',
    permission_title: 'فعِّل الوصول إلى الموقع',
    permission_body: 'للدفع على طاولتك، اسمح بالوصول إلى موقعك.',
    permission_cta: 'السماح',
    outside_title: 'يبدو أنك خارج المطعم',
    outside_body: 'اطلب من النادل أخذ دفعتك.',
    outside_call_waiter: 'نادِ النادل',
    outside_retry: 'حاول مرة أخرى',
    unsupported_title: 'الموقع غير متاح',
    unsupported_body: 'متصفحك لا يدعم تحديد الموقع الجغرافي. اطلب من النادل إتمام طلبك.',
    inconsistent_title: 'موقع غير دقيق',
    inconsistent_body: 'دقة GPS لديك غير كافية. اخرج إلى الهواء الطلق وحاول مجددًا، أو نادِ النادل.',
    verified_badge: 'تم التحقق على بُعد {{distance}} م',
  },
  zazu_verify: {
    success_title: 'تم تأكيد الدفع',
    success_body: 'تم استلام دفعتك بنجاح.',
    pending_title: 'جارٍ التحقق من الدفع',
    pending_body: 'قد يستغرق تأكيد دفعتك بضع ثوانٍ. يمكنك التحديث أو الاطلاع على طلباتك.',
    pending_cta_retry: 'تحقق الآن',
    pending_cta_orders: 'عرض طلباتي',
    canceled_title: 'تم إلغاء الدفع',
    canceled_body: 'تم حفظ سلتك.',
    canceled_cta_back: 'العودة إلى السلة',
    failed_title: 'فشل الدفع',
    failed_body: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
    failed_cta_retry: 'إعادة المحاولة',
    error_title: 'تعذر التحقق',
    error_body: 'لا يوجد اتصال بالشبكة. يرجى المحاولة مرة أخرى بعد لحظة.',
    error_cta_retry: 'إعادة المحاولة',
  },
  qr_bill: {
    error_missing_token: 'رابط الفاتورة مفقود.',
    error_expired: 'انتهت صلاحية هذه الفاتورة.',
    error_unavailable: 'الفاتورة {{status}}.',
    error_payment_init: 'فشل بدء عملية الدفع.',
    error_no_payment_url: 'لم يتم استلام رابط الدفع.',
    bill_unavailable_title: 'الفاتورة غير متاحة',
    back_cta: 'رجوع',
    item_fallback: 'عنصر',
    subtotal: 'المجموع الفرعي',
    tax: 'الضريبة',
    tip_legend: 'إكرامية (اختيارية)',
    tip_custom: 'مخصص',
    tip_custom_placeholder: 'إكرامية بـ {{currency}}',
    contact_legend: 'بريد إلكتروني أو هاتف (للإيصال الضريبي)',
    contact_email_label: 'بريد',
    contact_sms_label: 'SMS',
    email_placeholder: 'you@example.com',
    phone_placeholder: '+212 6XX XXX XXX',
    total_label: 'المجموع',
    pay_cta: 'ادفع {{amount}}',
    pay_opening: 'جارٍ فتح صفحة الدفع…',
    pay_methods: 'Apple Pay · Google Pay · بطاقة عبر ZAZU',
    confirming_title: 'جارٍ تأكيد الدفع…',
    confirming_body: 'يستغرق هذا عادة بضع ثوانٍ.',
    rating_title: 'شكرًا على الدفع',
    rating_body: 'كيف كانت تجربتك؟',
    rating_low_placeholder: 'ما الذي لم يكن جيدًا؟ (اختياري)',
    rating_send_feedback: 'إرسال الملاحظات',
    rating_skip: 'لا، شكرًا',
    done_title: 'تم بنجاح',
    done_body: 'سيصلك إيصالك الضريبي قريبًا.',
  },
  review_prompt: {
    aria_stars: 'تقييم 5 من 5 نجوم',
    title: 'كيف كانت تجربتك؟',
    body_with_store: 'ساعد {{store}} بمشاركة رأيك.',
    body_generic: 'شارك تجربتك.',
    review_google: 'اترك تقييمًا على Google',
    review_unconfigured: 'التقييمات لم يتم تهيئتها بعد',
    review_tripadvisor: 'اترك تقييمًا على TripAdvisor',
    coming_soon: '(قريبًا)',
    skip_cta: 'لاحقًا',
  },
  gift_card_purchase: {
    aria_back: 'رجوع',
    title: 'بطاقة هدية',
    hero_title: 'أهدِ بطاقة هدية',
    hero_valid_at: 'صالحة لدى {{store}}',
    section_amount: 'المبلغ',
    custom_amount_label: 'مبلغ مخصص',
    recipient_toggle: 'هل هي لشخص آخر؟',
    recipient_email_label: 'بريد المستلم',
    recipient_email_placeholder: 'email@example.com',
    message_label: 'رسالة (اختياري)',
    message_placeholder: 'أضف كلمة شخصية…',
    error_invalid_form: 'تحقق من المبلغ والبريد الإلكتروني للمستلم.',
    error_no_session: 'جلسة الدفع غير متاحة.',
    cta_buy: 'شراء',
    footer_email_recipient: 'سيتلقى المستلم الرمز عبر البريد الإلكتروني.',
    payment_title: 'الدفع',
    payment_total: 'المجموع',
    payment_processing: 'جارٍ المعالجة…',
    payment_pay_cta: 'ادفع — {{amount}}',
    payment_failed: 'فشلت عملية الدفع.',
  },
  gift_card_redeem: {
    aria_back: 'رجوع',
    title: 'بطاقة هدية',
    hero_balance_label: 'الرصيد المتبقي',
    hero_default_code: 'TPN-XXX-XXX',
    code_label: 'رمز بطاقتك',
    code_placeholder: 'TPN-XXXX-YYYY',
    code_email_hint: 'تجد الرمز في البريد الإلكتروني الذي استلمته.',
    code_invalid: 'رمز غير صالح أو منتهي الصلاحية.',
    expires_prefix: 'تنتهي في {{date}}',
    status_active: 'صالحة ✓',
    status_redeemed: 'مستخدمة بالفعل',
    status_expired: 'منتهية',
    status_cancelled: 'ملغاة',
    history_title: 'السجلّ',
    history_empty: 'لا يوجد استخدام حتى الآن.',
    error_no_store: 'امسح رمز QR للطاولة أولاً لاستخدام هذه البطاقة.',
    error_empty_cart: 'سلتك فارغة.',
    error_invalid: 'رمز غير صالح',
    cta_apply: 'استخدم على هذه الفاتورة',
    cta_applying: 'جارٍ التطبيق…',
    cta_applied: 'تم التطبيق — اعرض السلة',
    footer_or_at_checkout: 'أو أضف هذا الرمز عند الدفع.',
  },
  waiter_status: {
    requested: 'تم إرسال الطلب',
    ack: 'تم استلامه من النادل',
    en_route: 'في الطريق',
    resolved: 'تم',
  },
  order_tracking: {
    title: 'تتبع الطلب',
    item_pending: 'بانتظار',
    item_preparing: 'قيد التحضير',
    item_ready: 'جاهز',
    item_served: 'تم التقديم',
  },
  tip_chips: {
    title: 'هل تريد إضافة بقشيش؟',
    custom: 'مخصص',
    suggested: 'مقترح',
  },
  item_notes: {
    placeholder: 'ملاحظات خاصة (مثل: بدون بصل)',
    label: 'ملاحظات خاصة',
  },
  recommendations: {
    title: 'يُطلب عادةً مع',
  },
  whatsapp_share: {
    btn: 'مشاركة عبر واتساب',
    message: 'هذه حصتي: {{amount}} درهم — {{link}}',
  },
  order_ahead: {
    title: 'اطلب مسبقًا',
    pickup_time: 'وقت الاستلام',
    name: 'اسمك',
    phone: 'الهاتف',
    btn_submit: 'تأكيد الطلب',
    confirmation: 'تم تأكيد الطلب لـ {{time}}',
  },
  stock_badge: {
    low_stock: 'بقي {{n}} فقط',
  },
  promotions_client: {
    label: 'عرض -{{pct}}٪',
  },
};

export const messages: Record<Locale, Messages> = { fr, en, ar };

export const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(['ar']);

export type MessageKey = NestedKey<Messages>;

type NestedKey<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? P extends ''
      ? K
      : `${P}.${K}`
    : NestedKey<T[K], P extends '' ? K : `${P}.${K}`>;
}[keyof T & string];
